import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '@/utils/api';
import EmailEditor from './EmailEditor';
import EmailBlockBuilder from './EmailBlockBuilder';
import ConfirmDialog from './ui/confirm-dialog';
import { AddUserDialog, ConfirmDialog as ConfirmModal } from './ui/modal-dialog';
import { 
  Users, 
  BookOpen, 
  TrendingUp, 
  Smartphone,
  Tablet,
  Monitor, 
  Calendar,
  CalendarClock,
  Repeat,
  ArrowLeft,
  Mail,
  Star,
  Clock,
  Download,
  Lock,
  LogOut,
  BarChart3,
  Trash2,
  Trophy,
  Flame,
  X,
  LogIn,
  Globe,
  TrendingDown,
  Settings,
  RefreshCw,
  Eye,
  MousePointer,
  MousePointerClick,
  Target,
  Bell,
  Send,
  Plus,
  Upload,
  Image as ImageIcon,
  CheckCircle,
  AlertCircle,
  Pause,
  UserPlus,
  Heart,
  Mic,
  Brain,
  Zap,
  Activity,
  PieChart as PieChartIcon,
  FileText,
  Type,
  Building2,
  Search,
  MoreVertical,
  Edit,
  Pencil,
  UserCheck,
  UserX,
  ChevronRight,
  ChevronDown,
  CreditCard,
  Folder,
  Shield,
  Megaphone,
  LayoutDashboard,
  ExternalLink,
  Smile,
  Sparkles,
  Loader2,
  Gift,
  Volume2,
  VolumeX,
  Crown,
  User,
  Music,
  Video,
  Settings2,
  Link2,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { toast } from 'sonner';
// Toaster is rendered globally in App.js
import BannerManager from '@/components/admin/BannerManager';
import MotivationalVideosManager from '@/components/admin/MotivationalVideosManager';
import PromoCampaignManager from '@/components/admin/PromoCampaignManager';
import SubscriptionManager from '@/components/admin/SubscriptionManager';
import TierManager from '@/components/admin/TierManager';
import TierPreview from '@/components/admin/TierPreview';
import { DocumentationHub, TierLabelsSettings, SignupSettings, ReferralSourcesSettings, OnboardingManagement, DashboardDisplaySettings, TierEditor, LandingPageSettings } from '@/components/admin/SettingsManager';
import VideoEmbedsManagerUnified from '@/components/VideoEmbedsManagerUnified';
import SecurityManager from '@/components/admin/SecurityManager';
import MoodAnalyticsDashboard from '@/components/MoodAnalyticsDashboard';
import DOMPurify from 'dompurify';
import ClientsTab from '@/components/admin/tabs/ClientsTab';
import InactiveAccountsTab from '@/components/admin/InactiveAccountsTab';
import TrafficAnalyticsTab from '@/components/admin/tabs/TrafficAnalyticsTab';
import NotificationsTabContent from '@/components/admin/tabs/NotificationsTabContent';
import InvestorToolsTab from '@/components/admin/tabs/InvestorToolsTab';
import SystemEmailTemplates from '@/components/admin/SystemEmailTemplates';
import CreateUserModal from '@/components/admin/CreateUserModal';
import BulkEmailCampaign from '@/components/admin/BulkEmailCampaign';
import CampaignAnalytics from '@/components/admin/CampaignAnalytics';
import ReferralManagement from '@/components/admin/ReferralManagement';
// AcquisitionAnalytics removed - feature disabled
import FacebookPixelManager from '@/components/admin/FacebookPixelManager';
import EmailAnalyticsDashboard from '@/components/admin/EmailAnalyticsDashboard';
import LiveFunnelWidget from '@/components/admin/LiveFunnelWidget';
import CollapsibleSection from '@/components/admin/CollapsibleSection';
import DataCleanupManager from '@/components/admin/DataCleanupManager';
import EntryEngagementManager from '@/components/admin/EntryEngagementManager';
import PartnershipsManager from '@/components/admin/PartnershipsManager';

// Import shared admin components
import {
  MetricCard,
  MiniChart,
  MoodChart,
  FeatureAdoptionCard,
  ReferralSourceChart,
  TrafficSourcesPieChart,
  WorldMap,
  VoiceUsersModal,
  AIUsersModal,
  UserRow,
  UserDetailModal
} from '@/components/admin/shared';

// Dynamic API URL - uses relative paths for production compatibility
const API_BASE = '';

// NOTE: The following inline component definitions have been moved to /app/frontend/src/components/admin/shared/
// MetricCard, MiniChart, MoodChart, ReferralSourceChart, TrafficSourcesPieChart, 
// FeatureAdoptionCard, WorldMap, VoiceUsersModal, AIUsersModal, UserDetailModal, UserRow
// These components are now imported above from '@/components/admin/shared'

// Main Admin Dashboard Component
// Main Admin Dashboard Component

// Helper to format UTC date string without timezone conversion
const formatUTCDate = (isoString) => {
  if (!isoString) return 'N/A';
  try {
    // Extract just the date part (YYYY-MM-DD) and format it
    const datePart = isoString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  } catch {
    return 'N/A';
  }
};

// Helper to format ISO date string to user's local date
const formatLocalDate = (isoString) => {
  if (!isoString) return 'N/A';
  try {
    const date = new Date(isoString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  } catch {
    return 'N/A';
  }
};

// Helper to format hour to 12-hour format (e.g., "14" -> "2 PM", "9" -> "9 AM")
const formatHourTo12 = (hour) => {
  if (hour === null || hour === undefined || hour === 'N/A') return 'N/A';
  const h = parseInt(hour);
  if (isNaN(h)) return hour;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12} ${suffix}`;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [users, setUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [includeB2BUsers, setIncludeB2BUsers] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [trafficAnalytics, setTrafficAnalytics] = useState(null);
  const [locations, setLocations] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [referralDateRange, setReferralDateRange] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Automated Email Templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateEditorMode, setTemplateEditorMode] = useState('blocks');
  
  // Automations state (comprehensive workflow system)
  const [automations, setAutomations] = useState([]);
  const [automationTriggers, setAutomationTriggers] = useState({});
  const [emailVariables, setEmailVariables] = useState({});
  const [selectedAutomationCategory, setSelectedAutomationCategory] = useState('all');
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'announcement',
    show_popup: true,
    image_data: null,
    // New enhanced fields
    scheduled_at: '',
    expires_at: '',
    target_type: 'all', // 'all', 'specific', 'filter'
    target_user_ids: [],
    target_filter: '',
    action_buttons: [],
    use_personalization: false,
    target_user_search: '', // Search field for specific users
    send_as_email: false // Also send notification as email
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [targetingUsers, setTargetingUsers] = useState([]); // For user selection
  const [notificationAnalytics, setNotificationAnalytics] = useState(null);
  // Notification detail modal state
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationDetails, setNotificationDetails] = useState(null);
  const [loadingNotificationDetails, setLoadingNotificationDetails] = useState(false);
  const [notificationDetailTab, setNotificationDetailTab] = useState('stats'); // 'stats', 'recipients', 'views', 'clicks'
  
  // Analytics date range state
  const [analyticsDateRange, setAnalyticsDateRange] = useState('today'); // 'today', 'yesterday', '7days', '30days', 'all', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dailyBreakdown, setDailyBreakdown] = useState(null);
  const [showDailyBreakdownModal, setShowDailyBreakdownModal] = useState(false);
  const [showEntriesBreakdownModal, setShowEntriesBreakdownModal] = useState(false);
  
  // User tier editing state
  const [editingUserTier, setEditingUserTier] = useState(null); // { user_id, email, current_tier }
  const [updatingTier, setUpdatingTier] = useState(false);
  const [showLoginsBreakdownModal, setShowLoginsBreakdownModal] = useState(false);
  const [selectedDayDetails, setSelectedDayDetails] = useState(null);
  const [selectedDayEntries, setSelectedDayEntries] = useState(null);
  const [selectedDayLogins, setSelectedDayLogins] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [entryAnalytics, setEntryAnalytics] = useState(null);
  const [loadingEntryAnalytics, setLoadingEntryAnalytics] = useState(false);
  const [showEntryAnalyticsModal, setShowEntryAnalyticsModal] = useState(false);
  
  // Email state
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showCampaignAnalytics, setShowCampaignAnalytics] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConnected, setEmailConnected] = useState(null);
  const [emailFrom, setEmailFrom] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [newEmail, setNewEmail] = useState({
    subject: '',
    body_html: '',
    send_to: 'all',
    send_to_search: '',
    schedule_type: 'now', // 'now', 'scheduled', 'recurring'
    scheduled_at: '',
    recurrence: 'none' // 'none', 'weekly', 'monthly'
  });
  
  // Email scheduling state
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [emailStats, setEmailStats] = useState({ sent_today: 0, daily_limit: null, remaining: null, pending_scheduled: 0, plan_type: 'pro' });
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    loading: false
  });
  
  // Real-time signup alerts state
  const [signupAlerts, setSignupAlerts] = useState([]);
  const [signupAlertSettings, setSignupAlertSettings] = useState({
    sound_enabled: true,
    toast_enabled: true,
    badge_enabled: true,
    selected_sound: 'coin', // Default sound
    custom_sound_url: null
  });
  const [unseenSignupCount, setUnseenSignupCount] = useState(0);
  const [showSoundPicker, setShowSoundPicker] = useState(false);
  const lastSignupCheckRef = useRef(new Date().toISOString());
  const signupSoundRef = useRef(null);
  const customAudioRef = useRef(null);
  
  // Collapsible sections state (persisted to localStorage)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const stored = localStorage.getItem('admin_collapsed_sections');
    return stored ? JSON.parse(stored) : {};
  });
  
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => {
      const newState = { ...prev, [sectionId]: !prev[sectionId] };
      localStorage.setItem('admin_collapsed_sections', JSON.stringify(newState));
      return newState;
    });
  };
  
  // Preset sound definitions
  const PRESET_SOUNDS = {
    coin: { name: 'Coin Drop', icon: '🪙', description: 'Classic cash register coin sound' },
    chime: { name: 'Gentle Chime', icon: '🔔', description: 'Soft, pleasant bell chime' },
    success: { name: 'Success', icon: '✨', description: 'Uplifting achievement sound' },
    pop: { name: 'Pop', icon: '🎈', description: 'Quick, bubbly pop sound' },
    magic: { name: 'Magic Sparkle', icon: '🌟', description: 'Magical twinkling sound' }
  };
  
  // Initialize signup notification sounds
  useEffect(() => {
    const createSoundSystem = () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Sound 1: Coin Drop (original)
        const playCoin = () => {
          const now = audioContext.currentTime;
          const osc1 = audioContext.createOscillator();
          const gain1 = audioContext.createGain();
          osc1.type = 'sine';
          osc1.frequency.setValueAtTime(1200, now);
          osc1.frequency.exponentialRampToValueAtTime(800, now + 0.15);
          gain1.gain.setValueAtTime(0.3, now);
          gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
          osc1.connect(gain1);
          gain1.connect(audioContext.destination);
          osc1.start(now);
          osc1.stop(now + 0.4);
          
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(1800, now + 0.05);
          osc2.frequency.exponentialRampToValueAtTime(1200, now + 0.2);
          gain2.gain.setValueAtTime(0.15, now + 0.05);
          gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.start(now + 0.05);
          osc2.stop(now + 0.35);
          
          const osc3 = audioContext.createOscillator();
          const gain3 = audioContext.createGain();
          osc3.type = 'sine';
          osc3.frequency.setValueAtTime(1400, now + 0.15);
          osc3.frequency.exponentialRampToValueAtTime(1000, now + 0.35);
          gain3.gain.setValueAtTime(0.25, now + 0.15);
          gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
          osc3.connect(gain3);
          gain3.connect(audioContext.destination);
          osc3.start(now + 0.15);
          osc3.stop(now + 0.5);
        };
        
        // Sound 2: Gentle Chime
        const playChime = () => {
          const now = audioContext.currentTime;
          [523, 659, 784].forEach((freq, i) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now + i * 0.1);
            gain.gain.setValueAtTime(0.2, now + i * 0.1);
            gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.5);
            osc.connect(gain);
            gain.connect(audioContext.destination);
            osc.start(now + i * 0.1);
            osc.stop(now + i * 0.1 + 0.5);
          });
        };
        
        // Sound 3: Success (rising arpeggio)
        const playSuccess = () => {
          const now = audioContext.currentTime;
          [440, 554, 659, 880].forEach((freq, i) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(freq, now + i * 0.08);
            gain.gain.setValueAtTime(0.25, now + i * 0.08);
            gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.3);
            osc.connect(gain);
            gain.connect(audioContext.destination);
            osc.start(now + i * 0.08);
            osc.stop(now + i * 0.08 + 0.3);
          });
        };
        
        // Sound 4: Pop
        const playPop = () => {
          const now = audioContext.currentTime;
          const osc = audioContext.createOscillator();
          const gain = audioContext.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(600, now);
          osc.frequency.exponentialRampToValueAtTime(200, now + 0.1);
          gain.gain.setValueAtTime(0.4, now);
          gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
          osc.connect(gain);
          gain.connect(audioContext.destination);
          osc.start(now);
          osc.stop(now + 0.15);
        };
        
        // Sound 5: Magic Sparkle
        const playMagic = () => {
          const now = audioContext.currentTime;
          [1047, 1319, 1568, 2093, 1568, 1319].forEach((freq, i) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now + i * 0.06);
            gain.gain.setValueAtTime(0.15, now + i * 0.06);
            gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.06 + 0.2);
            osc.connect(gain);
            gain.connect(audioContext.destination);
            osc.start(now + i * 0.06);
            osc.stop(now + i * 0.06 + 0.2);
          });
        };
        
        return {
          context: audioContext,
          sounds: {
            coin: playCoin,
            chime: playChime,
            success: playSuccess,
            pop: playPop,
            magic: playMagic
          }
        };
      } catch (e) {
        console.warn('Audio context not supported:', e);
        return null;
      }
    };
    
    signupSoundRef.current = createSoundSystem();
    
    return () => {
      if (signupSoundRef.current?.context) {
        signupSoundRef.current.context.close();
      }
    };
  }, []);
  
  // Play the selected sound
  const playNotificationSound = (soundKey = null) => {
    const selectedSound = soundKey || signupAlertSettings.selected_sound || 'coin';
    
    // If custom sound is selected and we have a custom URL
    if (selectedSound === 'custom' && signupAlertSettings.custom_sound_url) {
      if (!customAudioRef.current) {
        customAudioRef.current = new Audio(signupAlertSettings.custom_sound_url);
      }
      customAudioRef.current.currentTime = 0;
      customAudioRef.current.play().catch(e => console.warn('Could not play custom sound:', e));
      return;
    }
    
    // Play preset sound
    if (signupSoundRef.current) {
      if (signupSoundRef.current.context.state === 'suspended') {
        signupSoundRef.current.context.resume();
      }
      const playFn = signupSoundRef.current.sounds[selectedSound];
      if (playFn) playFn();
    }
  };
  
  // Handle custom sound upload
  const handleCustomSoundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('audio/')) {
      toast.error('Please upload an audio file (MP3, WAV, etc.)');
      return;
    }
    
    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast.error('Sound file must be under 1MB');
      return;
    }
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Url = event.target.result;
        
        // Test the audio can play
        const testAudio = new Audio(base64Url);
        testAudio.oncanplaythrough = async () => {
          // Save to settings
          await updateSignupAlertSettings({ 
            custom_sound_url: base64Url,
            selected_sound: 'custom'
          });
          customAudioRef.current = testAudio;
          toast.success('Custom sound uploaded!');
        };
        testAudio.onerror = () => {
          toast.error('Could not load audio file');
        };
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to upload sound');
    }
  };
  
  // Fetch signup alert settings
  useEffect(() => {
    const fetchAlertSettings = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/signup-alerts/settings`);
        if (res.data?.settings) {
          setSignupAlertSettings(res.data.settings);
        }
      } catch (err) {
        console.error('Error fetching signup alert settings:', err);
      }
    };
    
    // Fetch recent signups from last 24 hours for initial display
    const fetchRecentSignups = async () => {
      try {
        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const res = await axios.get(`${API_BASE}/api/admin/signup-alerts`, {
          params: { since }
        });
        if (res.data?.success && res.data.alerts?.length > 0) {
          setSignupAlerts(res.data.alerts);
        }
      } catch (err) {
        console.error('Error fetching recent signups:', err);
      }
    };
    
    if (isAuthenticated) {
      fetchAlertSettings();
      fetchRecentSignups();
    }
  }, [isAuthenticated]);
  
  // Poll for new signups every 10 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const pollForSignups = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/signup-alerts`, {
          params: { since: lastSignupCheckRef.current }
        });
        
        if (res.data?.success && res.data.alerts?.length > 0) {
          const newAlerts = res.data.alerts;
          
          // Play sound for each new signup
          if (signupAlertSettings.sound_enabled) {
            playNotificationSound();
          }
          
          // Show toast notifications
          if (signupAlertSettings.toast_enabled) {
            newAlerts.forEach(alert => {
              toast.success(
                <div className="flex items-center gap-2">
                  <span className="text-lg">🎉</span>
                  <div>
                    <p className="font-medium">New {alert.tier_label}!</p>
                    <p className="text-sm opacity-80">{alert.name} just joined</p>
                  </div>
                </div>,
                { duration: 5000 }
              );
            });
          }
          
          // Update badge counter
          if (signupAlertSettings.badge_enabled) {
            setUnseenSignupCount(prev => prev + newAlerts.length);
          }
          
          // Add to alerts list
          setSignupAlerts(prev => [...newAlerts, ...prev].slice(0, 50));
        }
        
        // Update last check timestamp
        if (res.data?.checked_at) {
          lastSignupCheckRef.current = res.data.checked_at;
        }
      } catch (err) {
        // Silent fail - don't spam errors for polling
      }
    };
    
    // Initial poll
    pollForSignups();
    
    // Set up interval
    const interval = setInterval(pollForSignups, 10000); // Every 10 seconds
    
    return () => clearInterval(interval);
  }, [isAuthenticated, signupAlertSettings]);
  
  // Function to update signup alert settings
  const updateSignupAlertSettings = async (newSettings) => {
    try {
      const res = await axios.put(`${API_BASE}/api/admin/signup-alerts/settings`, newSettings);
      if (res.data?.settings) {
        setSignupAlertSettings(res.data.settings);
        toast.success('Alert settings updated');
      }
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };
  
  // Function to clear unseen count
  const clearUnseenSignups = () => {
    setUnseenSignupCount(0);
  };
  
  // Function to update user's membership tier
  const updateUserTier = async (userId, newTier) => {
    setUpdatingTier(true);
    try {
      await axios.put(`${API_BASE}/api/admin/users/${userId}/set-tier`, { tier: newTier });
      toast.success(`User tier updated to ${newTier}`);
      // Update local state in signupAlerts
      setSignupAlerts(prev => prev.map(alert => 
        alert.user_id === userId ? { ...alert, tier: newTier, tier_label: newTier.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) } : alert
      ));
      // Update local state in users list
      setUsers(prev => prev.map(user => 
        user.user_id === userId ? { ...user, membership_tier: newTier } : user
      ));
      setEditingUserTier(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tier');
    } finally {
      setUpdatingTier(false);
    }
  };
  
  // Campaign detail view state
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [loadingCampaignDetails, setLoadingCampaignDetails] = useState(false);
  const [campaignDetailTab, setCampaignDetailTab] = useState('stats'); // 'stats', 'preview', 'opens', 'clicks'
  
  // User detail modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEmailHistory, setUserEmailHistory] = useState([]);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  
  // Email analytics state
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  
  // Email composer state
  const [editorMode, setEditorMode] = useState('code'); // 'code' (rich text with buttons) or 'blocks'
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'mobile', 'tablet', 'desktop'
  
  // B2B Clients state
  const [clients, setClients] = useState([]);
  const [clientsTotals, setClientsTotals] = useState({ client_count: 0, total_seats: 0, total_activated: 0, total_revenue: 0 });
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadData, setBulkUploadData] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  // Create User Modal state (for adding members from dashboard overview)
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  // Full white-label theme configuration - EXPANDED 8-COLOR SYSTEM
  const DEFAULT_CLIENT_THEME = {
    // Core 4 colors
    brand_color: '#df1283',
    background_color: '#1a0527',
    card_color: '#2D063E',
    text_color: '#FFFFFF',
    // Additional 4 colors for depth
    border_color: '#4A0A66',
    accent_color: '#FF7901',
    secondary_text_color: '#FDF2F8',
    input_background: '#2D063E'
  };

  const [newClient, setNewClient] = useState({
    name: '',
    contact_email: '',
    contact_name: '',
    total_licenses: 100,
    price_paid: 0,
    currency: 'USD',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    app_name: 'Journal',
    ai_coach_name: 'Your Journal Coach',
    theme: { ...DEFAULT_CLIENT_THEME },
    features: {
      // Core Journal Features
      ai_coach: true,
      voice_typing: true,
      mood_tracking: true,
      image_attachments: true,
      pdf_export: true,
      link_preview: true,
      // Gamification
      gamification: true,
      streaks: true,
      badges: true,
      // Promotional & Marketing
      sidebar_promo: true,
      main_promo_banner: true,
      push_notifications: true,
      referral_system: true,
      // Appearance
      themes: true,
      dark_mode: true,
      // Premium Features
      client_admin_portal: false,
      analytics_dashboard: false,
      // Branding Control (for white-label)
      show_powered_by: true,
      show_upgrade_prompts: true,
      show_uncloned_branding: true
    },
    // White-label customization
    custom_domain: '',
    // Footer customization
    company_name: '',
    privacy_policy_url: '',
    terms_url: '',
    support_email: '',
    copyright_text: '',
    // Welcome email customization
    welcome_email_subject: '',
    welcome_email_template: ''
  });

  // Admin auth handler - calls backend to get token
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/api/admin/login`, { password }, { withCredentials: true });
      if (res.data.success) {
        // SECURITY: Auth token is now stored in httpOnly cookie by backend
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', 'true');
        fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid password');
    }
  };

  useEffect(() => {
    // Check for existing admin session via httpOnly cookie
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/verify`, {
          withCredentials: true
        });
        if (res.data.authenticated) {
          setIsAuthenticated(true);
          sessionStorage.setItem('adminAuth', 'true');
          fetchData();
          return;
        }
      } catch (err) {
        // Not authenticated
        sessionStorage.removeItem('adminAuth');
      }
    };
    checkAuth();
  }, []);

  // Track which tab data has been loaded (for lazy loading)
  const [loadedTabs, setLoadedTabs] = useState({
    overview: false,
    notifications: false,
    email: false,
    clients: false,
    traffic: false
  });

  // Fast initial load - only loads Overview tab data using combined endpoint
  const fetchData = async () => {
    setLoading(true);
    try {
      // Single combined endpoint for Overview tab - replaces 5+ API calls
      // Pass timezone offset for accurate local "today" calculations
      const tzOffset = -new Date().getTimezoneOffset() / 60;
      const initRes = await axios.get(`${API_BASE}/api/admin/dashboard-init?tz_offset=${tzOffset}`);
      
      if (initRes.data.success) {
        // Store initial data for locations, referrals, and daily breakdown
        setLocations(initRes.data.locations);
        setReferralStats(initRes.data.referral_stats);
        setDailyBreakdown(initRes.data.daily_breakdown);
        setLoadedTabs(prev => ({ ...prev, overview: true }));
        
        // Now fetch analytics filtered by the default date range ('today')
        // This ensures the metrics match the dropdown selection on initial load
        await fetchAnalyticsWithDateRange('today');
      }
      
      // Load users separately (needed for user search/management)
      const usersRes = await axios.get(`${API_BASE}/api/admin/users`);
      setUsers(usersRes.data);
      
      // Load entry analytics
      fetchEntryAnalytics('today');
      
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Lazy load notifications tab data
  const fetchNotificationsTabData = async () => {
    if (loadedTabs.notifications) return; // Already loaded
    try {
      const [notificationsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/notifications`).catch(() => ({ data: { notifications: [] } }))
      ]);
      setNotifications(notificationsRes.data?.notifications || []);
      fetchNotificationAnalytics();
      fetchTargetingUsers();
      setLoadedTabs(prev => ({ ...prev, notifications: true }));
    } catch (err) {
      console.error('Error fetching notifications data:', err);
    }
  };

  // Lazy load email tab data
  const fetchEmailTabData = async () => {
    if (loadedTabs.email) return; // Already loaded
    try {
      const [emailCampaignsRes, scheduledRes, emailStatsRes, emailAnalyticsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/email/campaigns`).catch(() => ({ data: { campaigns: [] } })),
        axios.get(`${API_BASE}/api/admin/email/scheduled`).catch(() => ({ data: { scheduled: [] } })),
        axios.get(`${API_BASE}/api/admin/email/stats`).catch(() => ({ data: { sent_today: 0, daily_limit: null, remaining: null, pending_scheduled: 0, plan_type: 'pro' } })),
        axios.get(`${API_BASE}/api/admin/email/analytics`).catch(() => ({ data: null }))
      ]);
      setEmailCampaigns(emailCampaignsRes.data?.campaigns || []);
      setScheduledEmails(scheduledRes.data?.scheduled || []);
      setEmailStats(emailStatsRes.data || { sent_today: 0, daily_limit: null, remaining: null, pending_scheduled: 0, plan_type: 'pro' });
      if (emailAnalyticsRes.data?.success) {
        setEmailAnalytics(emailAnalyticsRes.data);
      }
      setLoadedTabs(prev => ({ ...prev, email: true }));
    } catch (err) {
      console.error('Error fetching email data:', err);
    }
  };

  // Lazy load traffic tab data
  const fetchTrafficTabData = async () => {
    if (loadedTabs.traffic) return; // Already loaded
    try {
      const trafficRes = await axios.get(`${API_BASE}/api/admin/analytics/overview`).catch(() => ({ data: null }));
      setTrafficAnalytics(trafficRes.data);
      setLoadedTabs(prev => ({ ...prev, traffic: true }));
    } catch (err) {
      console.error('Error fetching traffic data:', err);
    }
  };

  // Fetch referral stats with date filter
  const fetchReferralStats = async (dateRange) => {
    try {
      let params = {};
      const today = new Date();
      const formatDate = (d) => d.toISOString().split('T')[0];
      
      switch (dateRange) {
        case 'today':
          params = { start_date: formatDate(today), end_date: formatDate(today) };
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          params = { start_date: formatDate(yesterday), end_date: formatDate(yesterday) };
          break;
        case '7days':
          const week = new Date(today);
          week.setDate(week.getDate() - 7);
          params = { start_date: formatDate(week), end_date: formatDate(today) };
          break;
        case '30days':
          const month = new Date(today);
          month.setDate(month.getDate() - 30);
          params = { start_date: formatDate(month), end_date: formatDate(today) };
          break;
        case 'all':
        default:
          params = {};
          break;
      }
      
      const res = await axios.get(`${API_BASE}/api/admin/referral-stats`, { params });
      setReferralStats(res.data);
    } catch (err) {
      console.error('Error fetching referral stats:', err);
    }
  };

  const handleDateRangeChange = (range) => {
    setReferralDateRange(range);
    fetchReferralStats(range);
  };

  // Notification functions
  const handleCreateNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      toast.error('Please fill in title and message');
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/api/admin/notifications`, newNotification);
      let statusMsg = res.data.status === 'scheduled' 
        ? `Notification scheduled for ${new Date(newNotification.scheduled_at).toLocaleString()}`
        : `Notification sent to ${res.data.users_notified} users`;
      
      // Add email info if emails were sent
      if (res.data.send_as_email && res.data.emails_sent > 0) {
        statusMsg += ` + ${res.data.emails_sent} emails sent`;
      }
      
      toast.success(statusMsg);
      setShowNotificationModal(false);
      resetNotificationForm();
      fetchData();
      fetchNotificationAnalytics();
    } catch (err) {
      toast.error('Failed to create notification');
    }
  };

  const resetNotificationForm = () => {
    setNewNotification({
      title: '',
      message: '',
      type: 'announcement',
      show_popup: true,
      image_data: null,
      scheduled_at: '',
      expires_at: '',
      target_type: 'all',
      target_user_ids: [],
      target_filter: '',
      action_buttons: [],
      use_personalization: false,
      target_user_search: '',
      send_as_email: false
    });
    setImagePreview(null);
  };

  const fetchTargetingUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/users/list`);
      setTargetingUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users for targeting:', err);
    }
  };

  const fetchNotificationAnalytics = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/notifications/analytics`);
      setNotificationAnalytics(res.data);
    } catch (err) {
      console.error('Error fetching notification analytics:', err);
    }
  };

  const addActionButton = () => {
    if (newNotification.action_buttons.length >= 2) {
      toast.error('Maximum 2 action buttons allowed');
      return;
    }
    setNewNotification({
      ...newNotification,
      action_buttons: [...newNotification.action_buttons, { text: '', url: '', style: 'primary' }]
    });
  };

  const updateActionButton = (index, field, value) => {
    const buttons = [...newNotification.action_buttons];
    buttons[index] = { ...buttons[index], [field]: value };
    setNewNotification({ ...newNotification, action_buttons: buttons });
  };

  const removeActionButton = (index) => {
    const buttons = newNotification.action_buttons.filter((_, i) => i !== index);
    setNewNotification({ ...newNotification, action_buttons: buttons });
  };

  const insertMergeTag = (tag) => {
    setNewNotification({
      ...newNotification,
      message: newNotification.message + tag,
      use_personalization: true
    });
  };

  // Fetch daily breakdown for analytics
  const fetchDailyBreakdown = async (days = 7) => {
    try {
      const tzOffset = -new Date().getTimezoneOffset() / 60;
      const res = await axios.get(`${API_BASE}/api/admin/analytics/daily-breakdown?days=${days}&tz_offset=${tzOffset}`);
      setDailyBreakdown(res.data.daily_breakdown);
    } catch (err) {
      console.error('Error fetching daily breakdown:', err);
    }
  };

  // Fetch analytics with date range filtering
  const fetchAnalyticsWithDateRange = async (dateRange, startDate = null, endDate = null) => {
    setLoadingAnalytics(true);
    try {
      // Get user's timezone offset in hours (e.g., -4 for EDT, -7 for PDT)
      const tzOffset = -new Date().getTimezoneOffset() / 60;
      
      let url = `${API_BASE}/api/admin/analytics?date_range=${dateRange}&tz_offset=${tzOffset}`;
      if (dateRange === 'custom' && startDate && endDate) {
        url = `${API_BASE}/api/admin/analytics?start_date=${startDate}&end_date=${endDate}&tz_offset=${tzOffset}`;
      }
      const res = await axios.get(url);
      setAnalytics(res.data);
      
      // Also fetch daily breakdown with appropriate days
      const days = dateRange === 'today' ? 1 : dateRange === 'yesterday' ? 2 : dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 7;
      fetchDailyBreakdown(days);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleAnalyticsDateRangeChange = (newRange) => {
    setAnalyticsDateRange(newRange);
    if (newRange !== 'custom') {
      fetchAnalyticsWithDateRange(newRange);
      fetchEntryAnalytics(newRange);
    }
  };

  const fetchEntryAnalytics = async (dateRange = 'today') => {
    setLoadingEntryAnalytics(true);
    try {
      const tzOffset = -new Date().getTimezoneOffset() / 60;
      const res = await axios.get(`${API_BASE}/api/admin/entry-analytics?date_range=${dateRange}&tz_offset=${tzOffset}`, {
        withCredentials: true
      });
      setEntryAnalytics(res.data);
    } catch (err) {
      console.error('Error fetching entry analytics:', err);
    } finally {
      setLoadingEntryAnalytics(false);
    }
  };

  const applyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      fetchAnalyticsWithDateRange('custom', customStartDate, customEndDate);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewNotification({ ...newNotification, image_data: reader.result });
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API_BASE}/api/admin/notifications/${notificationId}`);
      toast.success('Notification deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete notification');
    }
  };

  const openNotificationDetails = async (notification) => {
    setSelectedNotification(notification);
    setNotificationDetailTab('stats');
    setLoadingNotificationDetails(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/notifications/${notification.notification_id}/analytics`);
      setNotificationDetails(res.data);
    } catch (err) {
      console.error('Error fetching notification details:', err);
      toast.error('Failed to load notification details');
    } finally {
      setLoadingNotificationDetails(false);
    }
  };

  const closeNotificationDetails = () => {
    setSelectedNotification(null);
    setNotificationDetails(null);
  };

  // Fetch detailed campaign analytics when selecting a campaign
  const fetchCampaignDetails = async (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignDetailTab('stats');
    setLoadingCampaignDetails(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/email/analytics/${campaign.campaign_id}`);
      if (res.data.success) {
        setCampaignDetails(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch campaign details:', err);
    } finally {
      setLoadingCampaignDetails(false);
    }
  };

  // Fetch unsubscribed users
  const [unsubscribedUsers, setUnsubscribedUsers] = useState([]);
  const [showUnsubscribedModal, setShowUnsubscribedModal] = useState(false);
  const fetchUnsubscribedUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/email/unsubscribed-users`);
      if (res.data.success) {
        setUnsubscribedUsers(res.data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch unsubscribed users:', err);
    }
  };

  // Fetch user details for the user detail modal
  const fetchUserDetails = async (user) => {
    setSelectedUser(user);
    setLoadingUserDetails(true);
    try {
      // Fetch user's email interaction history
      const res = await axios.get(`${API_BASE}/api/admin/user/${user.user_id}/email-history`);
      if (res.data.success) {
        setUserEmailHistory(res.data.history || []);
        // Update selectedUser with full details
        if (res.data.user) {
          setSelectedUser(prev => ({ ...prev, ...res.data.user }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch user email history:', err);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  // Email functions
  const handleSendEmail = async () => {
    if (!newEmail.subject || !newEmail.body_html) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    // Handle scheduling
    if (newEmail.schedule_type === 'scheduled' || newEmail.schedule_type === 'recurring') {
      if (!newEmail.scheduled_at) {
        toast.error('Please select a date and time for scheduling');
        return;
      }
      
      try {
        setSendingEmail(true);
        setEmailStatus(null);
        
        const schedulePayload = {
          subject: newEmail.subject,
          body_html: newEmail.body_html,
          send_to: newEmail.send_to || 'all',
          scheduled_at: new Date(newEmail.scheduled_at).toISOString(),
          recurrence: newEmail.schedule_type === 'recurring' ? newEmail.recurrence : 'none',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        
        const res = await axios.post(`${API_BASE}/api/admin/email/schedule`, schedulePayload);
        
        if (res.data.success) {
          const scheduleMsg = newEmail.schedule_type === 'recurring' 
            ? `Email scheduled (${newEmail.recurrence}) starting ${format(new Date(newEmail.scheduled_at), 'MMM d, yyyy h:mm a')}`
            : `Email scheduled for ${format(new Date(newEmail.scheduled_at), 'MMM d, yyyy h:mm a')}`;
          setEmailStatus({ type: 'success', message: scheduleMsg });
          toast.success(scheduleMsg);
          setShowEmailModal(false);
          setNewEmail({ subject: '', body_html: '', send_to: 'all', send_to_search: '', schedule_type: 'now', scheduled_at: '', recurrence: 'none' });
          fetchData();
        } else {
          setEmailStatus({ type: 'error', message: res.data.error || 'Failed to schedule email' });
          toast.error(res.data.error || 'Failed to schedule email');
        }
      } catch (err) {
        const errorMsg = err.response?.data?.error || 'Failed to schedule email';
        setEmailStatus({ type: 'error', message: errorMsg });
        toast.error(errorMsg);
      } finally {
        setSendingEmail(false);
      }
      return;
    }
    
    // Send immediately (existing logic)
    try {
      setSendingEmail(true);
      setEmailStatus(null);
      const res = await axios.post(`${API_BASE}/api/admin/email/send`, {
        subject: newEmail.subject,
        body_html: newEmail.body_html,
        send_to: newEmail.send_to || 'all'
      });
      if (res.data.success) {
        setEmailStatus({ type: 'success', message: `Email sent to ${res.data.sent_count} users!` });
        toast.success(`Email sent to ${res.data.sent_count} users`);
        setShowEmailModal(false);
        setNewEmail({ subject: '', body_html: '', send_to: 'all', send_to_search: '', schedule_type: 'now', scheduled_at: '', recurrence: 'none' });
        fetchData();
      } else {
        setEmailStatus({ type: 'error', message: res.data.error || 'Failed to send email' });
        toast.error(res.data.error || 'Failed to send email');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to send email';
      setEmailStatus({ type: 'error', message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Cancel scheduled email
  const handleCancelScheduled = async (campaignId) => {
    try {
      const res = await axios.delete(`${API_BASE}/api/admin/email/scheduled/${campaignId}`);
      if (res.data.success) {
        toast.success('Scheduled email cancelled');
        fetchData();
      } else {
        toast.error(res.data.error || 'Failed to cancel');
      }
    } catch (err) {
      toast.error('Failed to cancel scheduled email');
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestingConnection(true);
      setEmailConnected(null); // Reset to loading state
      const res = await axios.get(`${API_BASE}/api/admin/email/test`);
      if (res.data.success) {
        setEmailConnected(true);
        setEmailFrom(res.data.email);
        toast.success(res.data.message);
      } else {
        setEmailConnected(false);
        toast.error(res.data.error || 'Resend connection failed');
      }
    } catch (err) {
      setEmailConnected(false);
      toast.error('Failed to test Resend connection');
    } finally {
      setTestingConnection(false);
    }
  };

  // Lazy load Email tab data and test email connection
  useEffect(() => {
    if (activeTab === 'email') {
      fetchEmailTabData();
      if (emailConnected === null) {
        handleTestEmail();
      }
    }
  }, [activeTab]);

  // Lazy load Notifications tab data
  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchNotificationsTabData();
    }
  }, [activeTab]);

  // Lazy load Traffic tab data
  useEffect(() => {
    if (activeTab === 'traffic') {
      fetchTrafficTabData();
    }
  }, [activeTab]);

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_BASE}/api/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (userId, revoke) => {
    try {
      await axios.post(`${API_BASE}/api/admin/users/${userId}/status`, { revoke });
      toast.success(revoke ? 'User revoked' : 'User restored');
      fetchData();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  // B2B Clients functions
  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const res = await axios.get(`${API_BASE}/api/admin/clients`);
      setClients(res.data.clients || []);
      setClientsTotals(res.data.totals || { client_count: 0, total_seats: 0, total_activated: 0, total_revenue: 0 });
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.contact_email) {
      toast.error('Please fill in client name and contact email');
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/api/admin/clients`, newClient);
      toast.success(`Client created! Portal password: ${res.data.client.portal_password}`);
      setShowClientModal(false);
      resetClientForm();
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create client');
    }
  };

  const resetClientForm = () => {
    setNewClient({
      name: '',
      contact_email: '',
      contact_name: '',
      total_licenses: 100,
      price_paid: 0,
      currency: 'USD',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      app_name: 'Journal',
      ai_coach_name: 'Your Journal Coach',
      theme: { ...DEFAULT_CLIENT_THEME },
      features: {
        ai_coach: true,
        voice_typing: true,
        mood_tracking: true,
        gamification: true,
        streaks: true,
        badges: true,
        pdf_export: true,
        image_attachments: true,
        themes: true
      }
    });
  };

  const handleViewClient = async (clientId) => {
    try {
      setLoadingClients(true);
      const res = await axios.get(`${API_BASE}/api/admin/clients/${clientId}`);
      setSelectedClient(res.data);
    } catch (err) {
      toast.error('Failed to load client details');
    } finally {
      setLoadingClients(false);
    }
  };

  const handleInviteUser = async (clientId, email, firstName, lastName) => {
    try {
      await axios.post(`${API_BASE}/api/admin/clients/${clientId}/invite-user`, {
        email,
        first_name: firstName,
        last_name: lastName
      });
      toast.success(`Invitation sent to ${email}`);
      handleViewClient(clientId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleBulkInvite = async (clientId) => {
    if (bulkUploadData.length === 0) {
      toast.error('No users to invite');
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/api/admin/clients/${clientId}/bulk-invite`, {
        users: bulkUploadData
      });
      toast.success(`Invited ${res.data.invited} users, skipped ${res.data.skipped}`);
      setShowBulkUploadModal(false);
      setBulkUploadData([]);
      handleViewClient(clientId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitations');
    }
  };

  const handleResendInvite = async (clientId, licenseUserId) => {
    try {
      await axios.post(`${API_BASE}/api/admin/clients/${clientId}/resend-invite/${licenseUserId}`);
      toast.success('Invitation resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const emailIdx = headers.findIndex(h => h.includes('email'));
    const firstNameIdx = headers.findIndex(h => h.includes('first') || h.includes('fname'));
    const lastNameIdx = headers.findIndex(h => h.includes('last') || h.includes('lname'));
    
    if (emailIdx === -1) {
      toast.error('CSV must have an "email" column');
      return [];
    }
    
    const users = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const email = values[emailIdx];
      if (email && email.includes('@')) {
        users.push({
          email: email.toLowerCase(),
          first_name: firstNameIdx >= 0 ? values[firstNameIdx] : null,
          last_name: lastNameIdx >= 0 ? values[lastNameIdx] : null
        });
      }
    }
    return users;
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const users = parseCSV(event.target.result);
      setBulkUploadData(users);
      if (users.length > 0) {
        toast.success(`Parsed ${users.length} users from CSV`);
      }
    };
    reader.readAsText(file);
  };

  // Fetch clients when tab changes
  useEffect(() => {
    if (activeTab === 'clients') {
      fetchClients();
    }
  }, [activeTab]);

  // Fetch email templates and automations when automation tab is active
  useEffect(() => {
    if (activeTab === 'automation') {
      fetchEmailTemplates();
      fetchAutomations();
      fetchEmailVariables();
    }
  }, [activeTab]);

  const fetchEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/email-templates`);
      setEmailTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    }
  };
  
  const fetchAutomations = async () => {
    try {
      const [automationsRes, triggersRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/automations`),
        axios.get(`${API_BASE}/api/admin/automations/triggers`)
      ]);
      setAutomations(automationsRes.data.automations || []);
      setAutomationTriggers(triggersRes.data.triggers || {});
    } catch (err) {
      console.error('Failed to fetch automations:', err);
    }
  };
  
  const fetchEmailVariables = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/automations/variables`);
      setEmailVariables(res.data.variables || {});
    } catch (err) {
      console.error('Failed to fetch email variables:', err);
    }
  };
  
  const handleUpdateAutomation = async (automationType, data) => {
    try {
      await axios.put(`${API_BASE}/api/admin/automations/${automationType}`, data);
      toast.success('Automation updated successfully');
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to update automation');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      // Check if this is an automation (has automation_type) or legacy template
      const isAutomation = editingTemplate.automation_type && automationTriggers[editingTemplate.automation_type];
      
      if (isAutomation) {
        await axios.put(`${API_BASE}/api/admin/automations/${editingTemplate.automation_type}`, {
          subject: editingTemplate.subject,
          body_html: editingTemplate.body_html,
          enabled: editingTemplate.enabled
        });
        toast.success('Automation updated successfully');
        fetchAutomations();
      } else {
        await axios.put(`${API_BASE}/api/admin/email-templates/${editingTemplate.template_type}`, {
          subject: editingTemplate.subject,
          body_html: editingTemplate.body_html,
          enabled: editingTemplate.enabled
        });
        toast.success('Template updated successfully');
        fetchEmailTemplates();
      }
      
      setShowTemplateModal(false);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const filteredClients = useMemo(() => {
    if (!clientSearchQuery) return clients;
    const query = clientSearchQuery.toLowerCase();
    return clients.filter(c => 
      c.name?.toLowerCase().includes(query) ||
      c.contact_email?.toLowerCase().includes(query)
    );
  }, [clients, clientSearchQuery]);

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `uncloned_users_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-8 w-full max-w-md">
          {/* Logo */}
          <div className="w-full mb-6">
            <div 
              className="w-full h-24 animate-gradient-x mx-auto"
              style={{
                background: 'linear-gradient(90deg, #df1283 0%, #FF7901 25%, #df1283 50%, #FF7901 75%, #df1283 100%)',
                backgroundSize: '200% 100%',
                WebkitMaskImage: `url("/new-logo-homepage.png")`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: `url("/new-logo-homepage.png")`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
              }}
            />
          </div>
          
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-[#FDF2F8]/60 text-sm mt-1">Enter password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} data-testid="admin-login-form">
            <Input
              data-testid="admin-password-input"
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#4A0A66]/30 border-[#4A0A66] text-white mb-4"
            />
            {error && <p className="text-red-400 text-sm mb-4" data-testid="admin-login-error">{error}</p>}
            <Button data-testid="admin-login-submit" type="submit" className="w-full bg-gradient-to-r from-[#df1283] to-[#FF7901]">
              <Lock className="w-4 h-4 mr-2" />
              Access Dashboard
            </Button>
          </form>
          
          <button 
            onClick={() => navigate('/')}
            className="w-full mt-4 text-[#FDF2F8]/50 hover:text-white text-sm flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527] p-4 lg:p-8 overflow-x-hidden">
      {/* Toaster is rendered globally in App.js - removed duplicate here */}
      <div className="max-w-7xl mx-auto overflow-hidden">
        {/* Header with Logo - Matching Dashboard styling */}
        <header className="mb-4 lg:mb-6 pt-2 sm:pt-4 relative">
          <div className="flex flex-col items-center gap-2 lg:gap-4">
            <div className="flex flex-col lg:flex-row items-center justify-center lg:gap-4 w-full">
              {/* Mobile & Tablet: Full-width SVG Logo */}
              <div 
                className="lg:hidden w-full px-1 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => { setActiveTab('overview'); setOpenDropdown(null); }}
                title="Admin Dashboard Home"
              >
                <div 
                  className="w-full h-16 sm:h-20 md:h-24 animate-gradient-x"
                  style={{
                    background: 'linear-gradient(90deg, #df1283 0%, #FF7901 25%, #df1283 50%, #FF7901 75%, #df1283 100%)',
                    backgroundSize: '200% 100%',
                    WebkitMaskImage: `url("/new-logo-homepage.png")`,
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskImage: `url("/new-logo-homepage.png")`,
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                  }}
                />
              </div>
              {/* Desktop: Full-width Logo */}
              <div 
                className="hidden lg:flex items-center justify-center w-full px-16 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => { setActiveTab('overview'); setOpenDropdown(null); }}
                title="Admin Dashboard Home"
              >
                <div 
                  className="h-20 xl:h-24 flex-1 animate-gradient-x"
                  style={{
                    background: 'linear-gradient(90deg, #df1283 0%, #FF7901 25%, #df1283 50%, #FF7901 75%, #df1283 100%)',
                    backgroundSize: '200% 100%',
                    WebkitMaskImage: `url("/new-logo-dashboard.webp")`,
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskImage: `url("/new-logo-dashboard.webp")`,
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                  }}
                />
              </div>
            </div>
          </div>
          
          <h1 className="text-xl lg:text-2xl font-bold text-white text-center mt-4 mb-6">Admin Dashboard</h1>
          
          {/* Mobile Tab Dropdown - visible on small screens */}
          <div className="md:hidden mb-6 mt-2">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full px-4 py-3 rounded-xl text-white font-medium text-base appearance-none cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, rgba(223, 18, 131, 0.2) 0%, rgba(255, 119, 1, 0.2) 100%)',
                border: '1px solid rgba(223, 18, 131, 0.5)',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23df1283' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '20px',
                paddingRight: '44px',
              }}
              data-testid="admin-mobile-nav"
            >
              <option value="overview">📊 Overview</option>
              <option value="traffic">📈 Traffic & Analytics</option>
              <option value="notifications">🔔 Notifications</option>
              <option value="email">✉️ Email</option>
              <option value="email-templates">✉️ Email Templates</option>
              <option value="promos">🎯 Promos</option>
              <option value="subscriptions">💳 Subscriptions</option>
              <option value="inactive-accounts">👤 Accounts Management</option>
              <option value="data-cleanup">⚠️ Profile Data Cleanup</option>
              <option value="entry-engagement">📓 Entry Engagement</option>
              <option value="clients">🏢 B2B Clients</option>
              <option value="referral">🎁 Referral System</option>
              <option value="partnerships">🤝 Partnerships</option>
              <option value="investor-tools">📊 Investor Tools</option>
              <option value="docs">📁 Docs</option>
              <option value="settings">⚙️ Settings</option>
            </select>
          </div>

          {/* Desktop Tab Switcher - hidden on small screens */}
          <div className="hidden md:flex items-center justify-center gap-2 mt-2 mb-6 bg-[#2D063E]/50 p-1 rounded-lg flex-wrap mx-auto w-fit">
            {/* Dashboard Group */}
            <div className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'dashboard' ? null : 'dashboard')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                  ['overview', 'traffic', 'mood'].includes(activeTab)
                    ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white' 
                    : 'text-[#FDF2F8]/70 hover:text-white'
                }`}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
                <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === 'dashboard' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'dashboard' && (
                <div className="absolute top-full left-0 mt-1 bg-[#2D063E] border border-[#df1283]/30 rounded-lg shadow-xl z-50 min-w-[160px]">
                  <button
                    onClick={() => { setActiveTab('overview'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 rounded-t-lg ${activeTab === 'overview' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <BarChart3 className="w-4 h-4" /> Overview
                  </button>
                  <button
                    onClick={() => { setActiveTab('mood'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'mood' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Smile className="w-4 h-4" /> Mood Analytics
                  </button>
                  <button
                    onClick={() => { setActiveTab('traffic'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'traffic' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <TrendingUp className="w-4 h-4" /> Traffic & Analytics
                  </button>
                </div>
              )}
            </div>

            {/* Marketing Group */}
            <div className="relative">
              <button
                data-testid="admin-marketing-menu"
                onClick={() => setOpenDropdown(openDropdown === 'marketing' ? null : 'marketing')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                  ['notifications', 'email', 'promos', 'email-templates', 'email-analytics', 'fb-pixel'].includes(activeTab)
                    ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white' 
                    : 'text-[#FDF2F8]/70 hover:text-white'
                }`}
              >
                <Megaphone className="w-4 h-4" />
                Marketing
                <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === 'marketing' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'marketing' && (
                <div className="absolute top-full left-0 mt-1 bg-[#2D063E] border border-[#df1283]/30 rounded-lg shadow-xl z-50 min-w-[160px]">
                  <button
                    onClick={() => { setActiveTab('notifications'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 rounded-t-lg ${activeTab === 'notifications' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Bell className="w-4 h-4" /> Notifications
                  </button>
                  <button
                    data-testid="admin-email-tab"
                    onClick={() => { setActiveTab('email'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'email' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Mail className="w-4 h-4" /> Email
                  </button>
                  <button
                    onClick={() => { setActiveTab('promos'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'promos' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Target className="w-4 h-4" /> Promos
                  </button>
                  <button
                    data-testid="admin-email-templates-tab"
                    onClick={() => { setActiveTab('email-templates'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'email-templates' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Mail className="w-4 h-4" /> Email Templates
                  </button>
                  <button
                    onClick={() => { setActiveTab('email-analytics'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'email-analytics' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <TrendingUp className="w-4 h-4" /> Email Analytics
                  </button>
                  <button
                    data-testid="admin-fb-pixel-tab"
                    onClick={() => { setActiveTab('fb-pixel'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 rounded-b-lg ${activeTab === 'fb-pixel' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <BarChart3 className="w-4 h-4" /> Facebook Pixel
                  </button>
                </div>
              )}
            </div>

            {/* Business Group */}
            <div className="relative">
              <button
                data-testid="admin-business-menu"
                onClick={() => setOpenDropdown(openDropdown === 'business' ? null : 'business')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                  ['subscriptions', 'clients', 'inactive-accounts', 'data-cleanup', 'entry-engagement'].includes(activeTab)
                    ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white' 
                    : 'text-[#FDF2F8]/70 hover:text-white'
                }`}
              >
                <Building2 className="w-4 h-4" />
                Business
                <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === 'business' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'business' && (
                <div className="absolute top-full left-0 mt-1 bg-[#2D063E] border border-[#df1283]/30 rounded-lg shadow-xl z-50 min-w-[180px]">
                  <button
                    onClick={() => { setActiveTab('subscriptions'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 rounded-t-lg ${activeTab === 'subscriptions' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <CreditCard className="w-4 h-4" /> Subscriptions
                  </button>
                  <button
                    onClick={() => { setActiveTab('inactive-accounts'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'inactive-accounts' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <UserX className="w-4 h-4" /> Accounts Management
                  </button>
                  <button
                    data-testid="admin-data-cleanup-tab"
                    onClick={() => { setActiveTab('data-cleanup'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'data-cleanup' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <AlertCircle className="w-4 h-4" /> Profile Data Cleanup
                  </button>
                  <button
                    data-testid="admin-entry-engagement-tab"
                    onClick={() => { setActiveTab('entry-engagement'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'entry-engagement' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <BookOpen className="w-4 h-4" /> Entry Engagement
                  </button>
                  <button
                    data-testid="admin-b2b-clients-tab"
                    onClick={() => { setActiveTab('clients'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 rounded-b-lg ${activeTab === 'clients' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Building2 className="w-4 h-4" /> B2B Clients
                  </button>
                </div>
              )}
            </div>

            {/* Admin Group */}
            <div className="relative">
              <button
                data-testid="admin-system-menu"
                onClick={() => setOpenDropdown(openDropdown === 'admin' ? null : 'admin')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                  ['settings', 'security', 'docs', 'investor-tools', 'referral', 'partnerships'].includes(activeTab)
                    ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white' 
                    : 'text-[#FDF2F8]/70 hover:text-white'
                }`}
              >
                <Settings className="w-4 h-4" />
                Admin
                <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === 'admin' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'admin' && (
                <div className="absolute top-full left-0 mt-1 bg-[#2D063E] border border-[#df1283]/30 rounded-lg shadow-xl z-50 min-w-[160px]">
                  <button
                    onClick={() => { setActiveTab('settings'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 rounded-t-lg ${activeTab === 'settings' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Settings className="w-4 h-4" /> Settings
                  </button>
                  <button
                    onClick={() => { setActiveTab('security'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'security' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Shield className="w-4 h-4" /> Security
                  </button>
                  <button
                    onClick={() => { setActiveTab('docs'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'docs' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Folder className="w-4 h-4" /> Docs
                  </button>
                  <button
                    data-testid="admin-referral-system-tab"
                    onClick={() => { setActiveTab('referral'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'referral' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Gift className="w-4 h-4" /> Referral System
                  </button>
                  <button
                    data-testid="admin-partnerships-tab"
                    onClick={() => { setActiveTab('partnerships'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'partnerships' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <Building2 className="w-4 h-4" /> Partnerships
                  </button>
                  <button
                    data-testid="admin-investor-tools-tab"
                    onClick={() => { setActiveTab('investor-tools'); setOpenDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 ${activeTab === 'investor-tools' ? 'bg-[#df1283]/20 text-[#df1283]' : 'text-white hover:bg-[#df1283]/10'}`}
                  >
                    <BarChart3 className="w-4 h-4" /> Investor Tools
                  </button>
                  <div className="border-t border-[#4A0A66] my-1"></div>
                  <button
                    onClick={() => { sessionStorage.removeItem('adminAuth'); setIsAuthenticated(false); setOpenDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 rounded-b-lg text-red-400 hover:bg-red-500/10"
                    data-testid="admin-logout-btn"
                  >
                    <LogOut className="w-4 h-4" /> Log out
                  </button>
                </div>
              )}
            </div>
            
            {/* Signup Alert Controls - Next to main menu */}
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-white/20">
              {/* Sound Picker Button */}
              <button
                onClick={() => {
                  playNotificationSound();
                  toast.success(`🔔 ${PRESET_SOUNDS[signupAlertSettings.selected_sound]?.name || 'Custom'} sound`);
                }}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                title="Preview notification sound"
              >
                <span className="text-sm">{PRESET_SOUNDS[signupAlertSettings.selected_sound]?.icon || '🎵'}</span>
              </button>
              
              {/* Sound Toggle */}
              <button
                onClick={() => updateSignupAlertSettings({ sound_enabled: !signupAlertSettings.sound_enabled })}
                className={`p-2 rounded-lg transition-colors ${
                  signupAlertSettings.sound_enabled 
                    ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]' 
                    : 'bg-white/10 text-white/40'
                }`}
                title={signupAlertSettings.sound_enabled ? 'Sound ON - Click to mute' : 'Sound OFF - Click to unmute'}
              >
                {signupAlertSettings.sound_enabled ? (
                  <Volume2 className="w-4 h-4" />
                ) : (
                  <VolumeX className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </header>
          
          <div className="flex items-center justify-center gap-3 mb-8">
          </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#df1283] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'traffic' ? (
          /* Traffic Analytics Tab */
          <TrafficAnalyticsTab trafficAnalytics={trafficAnalytics} />
        ) : activeTab === 'mood' ? (
          /* Mood Analytics Tab */
          <MoodAnalyticsDashboard isAdmin={true} />
        ) : activeTab === 'notifications' ? (
          <NotificationsTabContent
            notifications={notifications}
            notificationAnalytics={notificationAnalytics}
            showNotificationModal={showNotificationModal}
            setShowNotificationModal={setShowNotificationModal}
            newNotification={newNotification}
            setNewNotification={setNewNotification}
            imagePreview={imagePreview}
            setImagePreview={setImagePreview}
            handleImageUpload={handleImageUpload}
            insertMergeTag={insertMergeTag}
            targetingUsers={targetingUsers}
            addActionButton={addActionButton}
            updateActionButton={updateActionButton}
            removeActionButton={removeActionButton}
            handleCreateNotification={handleCreateNotification}
            handleDeleteNotification={handleDeleteNotification}
            openNotificationDetails={openNotificationDetails}
            selectedNotification={selectedNotification}
            closeNotificationDetails={closeNotificationDetails}
            notificationDetailTab={notificationDetailTab}
            setNotificationDetailTab={setNotificationDetailTab}
            notificationDetails={notificationDetails}
            loadingNotificationDetails={loadingNotificationDetails}
            resetNotificationForm={resetNotificationForm}
          />
        ) : activeTab === 'email' ? (
          /* Email Tab */
          <div className="space-y-6 overflow-hidden">
            {/* Header with Compose Button */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <h2 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF7901]" />
                Email Users
              </h2>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                {/* Email Stats Badge */}
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-[#4A0A66]/30 rounded-lg border border-[#4A0A66]">
                  <span className="text-xs text-[#FDF2F8]/50">Today:</span>
                  <span className="text-sm font-medium text-white">
                    {emailStats.sent_today}{emailStats.daily_limit ? `/${emailStats.daily_limit}` : ' sent'}
                    {!emailStats.daily_limit && <span className="text-[#0FFFE2] ml-1 text-xs">(unlimited)</span>}
                  </span>
                  {emailStats.pending_scheduled > 0 && (
                    <span className="text-xs text-[#0FFFE2]">({emailStats.pending_scheduled} scheduled)</span>
                  )}
                </div>
                <Button 
                  data-testid="admin-email-test-connection"
                  onClick={handleTestEmail}
                  variant="outline"
                  size="sm"
                  className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
                  disabled={testingConnection}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${testingConnection ? 'animate-spin' : ''}`} />
                  {testingConnection ? 'Testing...' : 'Test'}
                </Button>
                <Button 
                  data-testid="admin-compose-email"
                  onClick={() => { setEmailStatus(null); setNewEmail({ subject: '', body_html: '', send_to: 'all', send_to_search: '', schedule_type: 'now', scheduled_at: '', recurrence: 'none' }); setShowEmailModal(true); }}
                  size="sm"
                  className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
                  disabled={emailConnected === false}
                >
                  <Plus className="w-4 h-4 mr-2" /> Compose Email
                </Button>
                <Button 
                  data-testid="admin-bulk-email-campaign"
                  onClick={() => setShowBulkEmailModal(true)}
                  size="sm"
                  className="bg-gradient-to-r from-[#0FFFE2] to-[#00C9B7] text-black hover:opacity-90"
                  disabled={emailConnected === false}
                >
                  <Target className="w-4 h-4 mr-2" /> Bulk Campaign
                </Button>
                <Button 
                  data-testid="admin-campaign-analytics"
                  onClick={() => setShowCampaignAnalytics(true)}
                  size="sm"
                  variant="outline"
                  className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
                >
                  <BarChart3 className="w-4 h-4 mr-2" /> Analytics
                </Button>
              </div>
            </div>

            {/* Resend Connection Status */}
            <div data-testid="admin-email-connection-status" className={`rounded-xl p-4 border ${
              emailConnected === true 
                ? 'bg-green-500/10 border-green-500/30' 
                : emailConnected === false 
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-gradient-to-br from-[#2D063E] to-[#1a0527] border-[#4A0A66]/50'
            }`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  emailConnected === true 
                    ? 'bg-green-500/20' 
                    : emailConnected === false 
                      ? 'bg-red-500/20'
                      : 'bg-[#0FFFE2]/20'
                }`}>
                  {emailConnected === true ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : emailConnected === false ? (
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  ) : (
                    <Mail className="w-5 h-5 text-[#0FFFE2]" />
                  )}
                </div>
                <div>
                  <h3 className="text-white font-medium">
                    {emailConnected === true 
                      ? 'Resend Connected Successfully!' 
                      : emailConnected === false 
                        ? 'Resend Connection Failed'
                        : 'Checking Resend Connection...'}
                  </h3>
                  <p className="text-sm text-[#FDF2F8]/60 mt-1">
                    {emailConnected === true 
                      ? `Sending from: ${emailFrom}. Each user receives an individual email - not a mass CC.`
                      : emailConnected === false 
                        ? 'Please check your Resend API key and domain verification.'
                        : 'Please wait while we verify your Resend connection...'}
                  </p>
                  {emailConnected === true && (
                    <p className="text-sm text-[#FDF2F8]/40 mt-2">
                      Tip: Use {"{{first_name}}"} in your email to personalize with the user's name.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Email Analytics Summary */}
            {emailAnalytics?.summary && (
              <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#0FFFE2]" />
                  Campaign Analytics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-white">{emailAnalytics.summary.total_sent}</div>
                    <div className="text-xs text-[#FDF2F8]/50">Emails Sent</div>
                  </div>
                  <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center group relative">
                    <div className="text-2xl font-bold text-[#0FFFE2]">{emailAnalytics.summary.open_rate}%</div>
                    <div className="text-xs text-[#FDF2F8]/50">Unique Opens*</div>
                    <div className="text-xs text-[#0FFFE2]/70">{emailAnalytics.summary.unique_opens || emailAnalytics.summary.total_opens} unique</div>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-xs text-[#FDF2F8]/80 w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      *Rate based on unique opens (one per recipient). Total opens including re-reads: {emailAnalytics.summary.total_opens}
                    </div>
                  </div>
                  <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-[#FF7901]">{emailAnalytics.summary.click_rate}%</div>
                    <div className="text-xs text-[#FDF2F8]/50">Click Rate</div>
                    <div className="text-xs text-[#FF7901]/70">{emailAnalytics.summary.total_clicks} clicks</div>
                  </div>
                  <div 
                    className="bg-[#4A0A66]/20 rounded-lg p-3 text-center cursor-pointer hover:bg-[#4A0A66]/30 transition-colors"
                    onClick={async () => {
                      await fetchUnsubscribedUsers();
                      setShowUnsubscribedModal(true);
                    }}
                    title="Click to view unsubscribed users"
                  >
                    <div className="text-2xl font-bold text-red-400">{emailAnalytics.summary.total_unsubscribes}</div>
                    <div className="text-xs text-[#FDF2F8]/50">Unsubscribes</div>
                    {emailAnalytics.summary.total_bounces > 0 && (
                      <div className="text-xs text-red-400/70">{emailAnalytics.summary.total_bounces} bounced</div>
                    )}
                    <div className="text-xs text-[#0FFFE2]/50 mt-1">Click to view</div>
                  </div>
                </div>
              </div>
            )}

            {/* Scheduled Emails Section */}
            {scheduledEmails.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-[#FDF2F8]/70 flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-[#0FFFE2]" />
                  Scheduled Emails ({scheduledEmails.length})
                </h3>
                {scheduledEmails.map((scheduled) => (
                  <div 
                    key={scheduled.campaign_id} 
                    className="bg-gradient-to-br from-[#1a0527] to-[#2D063E] border border-[#0FFFE2]/30 rounded-xl p-4 group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-medium">{scheduled.subject}</h4>
                          {scheduled.recurrence !== 'none' && (
                            <span className="px-2 py-0.5 bg-[#FF7901]/20 text-[#FF7901] text-xs rounded-full flex items-center gap-1">
                              <Repeat className="w-3 h-3" />
                              {scheduled.recurrence}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-[#FDF2F8]/50 flex-wrap">
                          <span className="flex items-center gap-1 text-[#0FFFE2]">
                            <Clock className="w-3 h-3" />
                            {format(new Date(scheduled.next_run_at || scheduled.scheduled_at), 'MMM d, yyyy h:mm a')}
                          </span>
                          <span className="px-2 py-0.5 bg-[#0FFFE2]/20 text-[#0FFFE2] text-xs rounded-full">
                            {scheduled.status === 'recurring' ? 'Recurring' : 'Scheduled'}
                          </span>
                          {scheduled.sent_count > 0 && (
                            <span className="text-[#FDF2F8]/40 text-xs">
                              {scheduled.sent_count} sent previously
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Cancel Scheduled Email?',
                            message: `Are you sure you want to cancel "${scheduled.subject}"? This scheduled email will not be sent.`,
                            type: 'warning',
                            loading: false,
                            onConfirm: async () => {
                              setConfirmDialog(prev => ({ ...prev, loading: true }));
                              try {
                                await handleCancelScheduled(scheduled.campaign_id);
                                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                              } catch (err) {
                                setConfirmDialog(prev => ({ ...prev, loading: false }));
                              }
                            }
                          });
                        }}
                        className="p-2 text-[#FDF2F8]/40 hover:text-[#FF7901] hover:bg-[#FF7901]/10 rounded-lg transition-colors"
                        title="Cancel scheduled email"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Campaign History */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-[#FDF2F8]/70">Recent Campaigns</h3>
              {emailCampaigns.length === 0 ? (
                <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-8 text-center">
                  <Mail className="w-12 h-12 text-[#FDF2F8]/30 mx-auto mb-3" />
                  <p className="text-[#FDF2F8]/50">No email campaigns yet</p>
                  <p className="text-sm text-[#FDF2F8]/30 mt-1">Send your first email to users</p>
                </div>
              ) : (
                emailCampaigns.map((campaign) => (
                  <div 
                    key={campaign.campaign_id} 
                    className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4 hover:border-[#FF7901]/50 transition-colors cursor-pointer group"
                    onClick={() => fetchCampaignDetails(campaign)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium group-hover:text-[#FF7901] transition-colors">{campaign.subject}</h4>
                        <div className="flex items-center gap-4 mt-2 text-sm text-[#FDF2F8]/50 flex-wrap">
                          <span>
                            {campaign.sent_at 
                              ? format(new Date(campaign.sent_at), 'MMM d, yyyy h:mm a')
                              : campaign.created_at 
                                ? format(new Date(campaign.created_at), 'MMM d, yyyy h:mm a')
                                : 'Unknown date'}
                          </span>
                          <span className="flex items-center gap-1 text-green-400">
                            <CheckCircle className="w-3 h-3" />
                            {campaign.sent_count} sent
                          </span>
                          {campaign.opens > 0 && (
                            <span className="flex items-center gap-1 text-[#0FFFE2]" title="Detected opens may be higher than actual views due to email client pre-fetching">
                              <Eye className="w-3 h-3" />
                              {campaign.opens} detected ({campaign.unique_opens?.length || 0} unique)
                            </span>
                          )}
                          {campaign.clicks > 0 && (
                            <span className="flex items-center gap-1 text-[#FF7901]">
                              <MousePointer className="w-3 h-3" />
                              {campaign.clicks} clicks
                            </span>
                          )}
                          {campaign.failed_count > 0 && (
                            <span className="flex items-center gap-1 text-red-400">
                              <AlertCircle className="w-3 h-3" />
                              {campaign.failed_count} failed
                            </span>
                          )}
                          <span className="text-[#0FFFE2]/70 text-xs">Click to view</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Delete Email Campaign?',
                            message: `Are you sure you want to delete "${campaign.subject}"? This action cannot be undone.`,
                            type: 'danger',
                            loading: false,
                            onConfirm: async () => {
                              setConfirmDialog(prev => ({ ...prev, loading: true }));
                              try {
                                await axios.delete(`${API_BASE}/api/admin/email/campaigns/${campaign.campaign_id}`);
                                setEmailCampaigns(prev => prev.filter(c => c.campaign_id !== campaign.campaign_id));
                                toast.success('Campaign deleted');
                                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                              } catch (err) {
                                toast.error('Failed to delete campaign');
                                setConfirmDialog(prev => ({ ...prev, loading: false }));
                              }
                            }
                          });
                        }}
                        className="p-2 text-[#FDF2F8]/40 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                        title="Delete campaign"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Campaign Detail Modal - Enhanced with Analytics */}
            {selectedCampaign && (
              <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-5xl mb-8 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-[#4A0A66]/50">
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedCampaign.subject || 'Untitled Campaign'}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#FDF2F8]/50 flex-wrap">
                        <span>
                          {selectedCampaign.sent_at 
                            ? format(new Date(selectedCampaign.sent_at), 'MMMM d, yyyy \'at\' h:mm a')
                            : 'Date not available'}
                        </span>
                        <span className="flex items-center gap-1 text-green-400">
                          <CheckCircle className="w-4 h-4" />
                          {selectedCampaign.sent_count || 0} delivered
                        </span>
                        {((selectedCampaign.opens || 0) > 0 || (campaignDetails?.unique_opens || 0) > 0) && (
                          <span className="flex items-center gap-1 text-[#0FFFE2]">
                            <Eye className="w-4 h-4" />
                            {campaignDetails?.unique_opens || selectedCampaign.unique_opens?.length || 0} opened
                          </span>
                        )}
                        {((selectedCampaign.clicks || 0) > 0 || (campaignDetails?.unique_clicks || 0) > 0) && (
                          <span className="flex items-center gap-1 text-[#FF7901]">
                            <MousePointer className="w-4 h-4" />
                            {campaignDetails?.unique_clicks || selectedCampaign.unique_clicks?.length || 0} clicked
                          </span>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={() => { setSelectedCampaign(null); setCampaignDetails(null); }}
                      className="p-2 text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  {/* Tabs */}
                  <div className="flex border-b border-[#4A0A66]/50 px-6">
                    {[
                      { id: 'stats', label: 'Stats', icon: BarChart3 },
                      { id: 'preview', label: 'Preview', icon: Mail },
                      { id: 'opens', label: `Detected Opens (${campaignDetails?.unique_opens || 0})`, icon: Eye },
                      { id: 'clicks', label: `Clicks (${campaignDetails?.unique_clicks || 0})`, icon: MousePointer },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setCampaignDetailTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                          campaignDetailTab === tab.id
                            ? 'text-[#0FFFE2] border-[#0FFFE2]'
                            : 'text-[#FDF2F8]/50 border-transparent hover:text-white'
                        }`}
                      >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Campaign Stats Bar */}
                  {!loadingCampaignDetails && (
                    <div className="px-6 py-3 bg-[#1a0527]/50 border-b border-[#4A0A66]/30 flex items-center gap-6 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Send className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-white">{selectedCampaign.sent_count || 0} <span className="text-[#FDF2F8]/50">sent</span></span>
                      </div>
                      <div className="flex items-center gap-2" title="Detected opens may be higher due to email client pre-fetching">
                        <Eye className="w-4 h-4 text-[#0FFFE2]" />
                        <span className="text-sm text-white">
                          {campaignDetails?.unique_opens || 0} <span className="text-[#FDF2F8]/50">detected*</span>
                          {(selectedCampaign.sent_count || 0) > 0 && (
                            <span className="text-[#0FFFE2] ml-1">
                              ({Math.round((campaignDetails?.unique_opens || 0) / (selectedCampaign.sent_count || 1) * 100)}%)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MousePointer className="w-4 h-4 text-[#FF7901]" />
                        <span className="text-sm text-white">
                          {campaignDetails?.unique_clicks || 0} <span className="text-[#FDF2F8]/50">clicked</span>
                          {(selectedCampaign.sent_count || 0) > 0 && (
                            <span className="text-[#FF7901] ml-1">
                              ({Math.round((campaignDetails?.unique_clicks || 0) / (selectedCampaign.sent_count || 1) * 100)}%)
                            </span>
                          )}
                        </span>
                      </div>
                      {(selectedCampaign.failed_count || 0) > 0 && (
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-400" />
                          <span className="text-sm text-red-400">{selectedCampaign.failed_count} <span className="text-red-400/50">failed</span></span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {loadingCampaignDetails ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FFFE2]"></div>
                      </div>
                    ) : (
                      <>
                        {/* Stats Tab */}
                        {campaignDetailTab === 'stats' && (
                          <div className="space-y-6">
                            {/* Main Stats Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                                <div className="text-3xl font-bold text-green-400">{selectedCampaign.sent_count || 0}</div>
                                <div className="text-sm text-[#FDF2F8]/50 mt-1">Delivered</div>
                              </div>
                              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center group relative">
                                <div className="text-3xl font-bold text-[#0FFFE2]">
                                  {(selectedCampaign.sent_count || 0) > 0 
                                    ? Math.round((campaignDetails?.unique_opens || 0) / (selectedCampaign.sent_count || 1) * 100)
                                    : 0}%
                                </div>
                                <div className="text-sm text-[#FDF2F8]/50 mt-1">Detected Open Rate*</div>
                                <div className="text-xs text-[#0FFFE2]/70">{campaignDetails?.unique_opens || 0} detected</div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-xs text-[#FDF2F8]/80 w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                  *May include pre-fetched opens from email clients
                                </div>
                              </div>
                              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                                <div className="text-3xl font-bold text-[#FF7901]">
                                  {(selectedCampaign.sent_count || 0) > 0 
                                    ? Math.round((campaignDetails?.unique_clicks || 0) / (selectedCampaign.sent_count || 1) * 100)
                                    : 0}%
                                </div>
                                <div className="text-sm text-[#FDF2F8]/50 mt-1">Click Rate</div>
                                <div className="text-xs text-[#FF7901]/70">{campaignDetails?.unique_clicks || 0} unique clicks</div>
                              </div>
                              <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                                <div className="text-3xl font-bold text-red-400">{selectedCampaign.failed_count || 0}</div>
                                <div className="text-sm text-[#FDF2F8]/50 mt-1">Failed</div>
                              </div>
                            </div>
                            
                            {/* Visual Funnel */}
                            <div className="bg-[#4A0A66]/10 rounded-xl p-6">
                              <h4 className="text-white font-medium mb-4">Engagement Funnel</h4>
                              <div className="space-y-3">
                                {/* Sent Bar */}
                                <div>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-[#FDF2F8]/70">Sent</span>
                                    <span className="text-white">{selectedCampaign.sent_count}</span>
                                  </div>
                                  <div className="h-8 bg-[#4A0A66]/30 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{ width: '100%' }}></div>
                                  </div>
                                </div>
                                {/* Opened Bar */}
                                <div>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-[#FDF2F8]/70">Detected Opens*</span>
                                    <span className="text-[#0FFFE2]">{campaignDetails?.unique_opens || 0}</span>
                                  </div>
                                  <div className="h-8 bg-[#4A0A66]/30 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-[#0FFFE2] to-[#0FFFE2]/70 rounded-full transition-all duration-500" 
                                      style={{ width: `${selectedCampaign.sent_count > 0 ? (campaignDetails?.unique_opens || 0) / selectedCampaign.sent_count * 100 : 0}%` }}
                                    ></div>
                                  </div>
                                </div>
                                {/* Clicked Bar */}
                                <div>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-[#FDF2F8]/70">Clicked</span>
                                    <span className="text-[#FF7901]">{campaignDetails?.unique_clicks || 0}</span>
                                  </div>
                                  <div className="h-8 bg-[#4A0A66]/30 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-[#FF7901] to-[#FF7901]/70 rounded-full transition-all duration-500" 
                                      style={{ width: `${selectedCampaign.sent_count > 0 ? (campaignDetails?.unique_clicks || 0) / selectedCampaign.sent_count * 100 : 0}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                              <p className="text-xs text-[#FDF2F8]/40 mt-2">*Detected opens may include pre-fetches by email clients (Apple Mail, Gmail, etc.)</p>
                            </div>
                            
                            {/* Recent Activity */}
                            {campaignDetails?.events?.length > 0 && (
                              <div className="bg-[#4A0A66]/10 rounded-xl p-6">
                                <h4 className="text-white font-medium mb-4">Recent Activity</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {campaignDetails.events.slice(0, 10).map((event, idx) => {
                                    const user = campaignDetails.engaged_users?.find(u => u.user_id === event.user_id);
                                    return (
                                      <div key={event.id || `event-${event.user_id}-${event.event_type}-${idx}`} className="flex items-center gap-3 text-sm">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                          event.event_type === 'open' ? 'bg-[#0FFFE2]/20' : 'bg-[#FF7901]/20'
                                        }`}>
                                          {event.event_type === 'open' 
                                            ? <Eye className="w-3 h-3 text-[#0FFFE2]" />
                                            : <MousePointer className="w-3 h-3 text-[#FF7901]" />
                                          }
                                        </div>
                                        <span className="text-white">{user?.first_name || user?.name || user?.email || 'User'}</span>
                                        <span className="text-[#FDF2F8]/40">{event.event_type === 'open' ? 'opened' : 'clicked'}</span>
                                        <span className="text-[#FDF2F8]/30 ml-auto text-xs">
                                          {format(new Date(event.timestamp), 'MMM d, h:mm a')}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Preview Tab */}
                        {campaignDetailTab === 'preview' && (
                          <div className="bg-gray-100 rounded-xl overflow-hidden p-4">
                            {/* Email Preview Container */}
                            <div className="max-w-[600px] mx-auto bg-white rounded-xl overflow-hidden shadow-lg">
                              {/* Header with logo */}
                              <div className="bg-gradient-to-br from-[#1a0527] to-[#2D063E] p-5 text-center">
                                <img 
                                  src="https://customer-assets-jt897jd0.emergentagent.net/job_daily-notes-84/artifacts/7qbqgk18_UnCloned%C2%AE%20Journal-Color%20Logo.png" 
                                  alt="UnCloned® Journal" 
                                  className="max-w-[200px] h-auto mx-auto"
                                />
                              </div>
                              
                              {/* Accent bar */}
                              <div className="h-1 bg-gradient-to-r from-[#df1283] to-[#FF7901]"></div>
                              
                              {/* Main content */}
                              <div 
                                className="p-6 text-gray-800 text-base leading-relaxed [&_p]:mb-4 [&_a]:text-[#df1283] [&_a]:no-underline [&_strong]:font-bold"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedCampaign.body_html?.replace(/<!-- BLOCKS:.*? -->\n?/g, '') || '') }} 
                              />
                              
                              {/* Footer */}
                              <div className="bg-gray-50 px-6 py-5 text-center border-t border-gray-200">
                                <p className="text-sm text-gray-600 mb-2">
                                  Sent with 💜 from <a href="https://unclonedjournal.com" className="text-[#df1283] font-semibold no-underline">UnCloned® Journal</a>
                                </p>
                                <p className="text-xs text-gray-400 mb-3">
                                  © 2026 UnCloned® Journal. All rights reserved.
                                </p>
                                <p className="text-xs text-gray-400">
                                  <span className="underline cursor-pointer">Unsubscribe from these emails</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Opens Tab */}
                        {campaignDetailTab === 'opens' && (
                          <div>
                            <h4 className="text-white font-medium mb-4">Users Who Opened This Email</h4>
                            {campaignDetails?.engaged_users?.filter(u => 
                              campaignDetails.events?.some(e => e.event_type === 'open' && e.user_id === u.user_id)
                            ).length > 0 ? (
                              <div className="space-y-2">
                                {campaignDetails.engaged_users
                                  .filter(u => campaignDetails.events?.some(e => e.event_type === 'open' && e.user_id === u.user_id))
                                  .map((user) => {
                                    const openEvent = campaignDetails.events?.find(e => e.event_type === 'open' && e.user_id === user.user_id);
                                    return (
                                      <div 
                                        key={user.user_id} 
                                        className="flex items-center justify-between bg-[#4A0A66]/20 rounded-lg p-3 cursor-pointer hover:bg-[#4A0A66]/40 transition-colors"
                                        onClick={() => fetchUserDetails(user)}
                                      >
                                        <div className="flex items-center gap-3">
                                          <div className="w-8 h-8 rounded-full bg-[#0FFFE2]/20 flex items-center justify-center">
                                            <Eye className="w-4 h-4 text-[#0FFFE2]" />
                                          </div>
                                          <div>
                                            <p className="text-white font-medium">{user.first_name || user.name || 'User'}</p>
                                            <p className="text-sm text-[#FDF2F8]/50">{user.email}</p>
                                          </div>
                                        </div>
                                        {openEvent && (
                                          <span className="text-xs text-[#FDF2F8]/40">
                                            {format(new Date(openEvent.timestamp), 'MMM d, h:mm a')}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-[#FDF2F8]/40">
                                <Eye className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>No opens detected yet</p>
                                <p className="text-xs mt-1">Opens are detected when recipients view the email (may include email client pre-fetches)</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Clicks Tab */}
                        {campaignDetailTab === 'clicks' && (
                          <div>
                            <h4 className="text-white font-medium mb-4">Users Who Clicked Links</h4>
                            {campaignDetails?.engaged_users?.filter(u => 
                              campaignDetails.events?.some(e => e.event_type === 'click' && e.user_id === u.user_id)
                            ).length > 0 ? (
                              <div className="space-y-2">
                                {campaignDetails.engaged_users
                                  .filter(u => campaignDetails.events?.some(e => e.event_type === 'click' && e.user_id === u.user_id))
                                  .map((user) => {
                                    const clickEvents = campaignDetails.events?.filter(e => e.event_type === 'click' && e.user_id === user.user_id) || [];
                                    return (
                                      <div 
                                        key={user.user_id} 
                                        className="bg-[#4A0A66]/20 rounded-lg p-3 cursor-pointer hover:bg-[#4A0A66]/40 transition-colors"
                                        onClick={() => fetchUserDetails(user)}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-[#FF7901]/20 flex items-center justify-center">
                                              <MousePointer className="w-4 h-4 text-[#FF7901]" />
                                            </div>
                                            <div>
                                              <p className="text-white font-medium">{user.first_name || user.name || 'User'}</p>
                                              <p className="text-sm text-[#FDF2F8]/50">{user.email}</p>
                                            </div>
                                          </div>
                                          <span className="text-xs text-[#FDF2F8]/40">{clickEvents.length} click(s)</span>
                                        </div>
                                        {clickEvents.length > 0 && (
                                          <div className="mt-2 pl-11 space-y-1">
                                            {clickEvents.slice(0, 3).map((event, idx) => (
                                              <div key={event.id || `click-${event.timestamp}-${idx}`} className="text-xs text-[#FDF2F8]/40 truncate">
                                                {event.url || 'Link clicked'} - {format(new Date(event.timestamp), 'MMM d, h:mm a')}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-[#FDF2F8]/40">
                                <MousePointer className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>No clicks tracked yet</p>
                                <p className="text-xs mt-1">Clicks are tracked when recipients click links in the email</p>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between p-6 border-t border-[#4A0A66]/50 bg-[#1a0527]/50">
                    <button
                      onClick={() => {
                        setNewEmail({
                          subject: selectedCampaign.subject,
                          body_html: selectedCampaign.body_html,
                          send_to: 'all',
                          send_to_search: '',
                          schedule_type: 'now',
                          scheduled_at: '',
                          recurrence: 'none'
                        });
                        setSelectedCampaign(null);
                        setCampaignDetails(null);
                        setShowEmailModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-[#0FFFE2] hover:bg-[#0FFFE2]/10 rounded-lg transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Duplicate & Edit
                    </button>
                    <button
                      onClick={() => { setSelectedCampaign(null); setCampaignDetails(null); }}
                      className="px-6 py-2 bg-[#4A0A66]/50 hover:bg-[#4A0A66] text-white rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Unsubscribed Users Modal */}
            {showUnsubscribedModal && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                  <div className="flex items-center justify-between p-6 border-b border-[#4A0A66]/50">
                    <div>
                      <h3 className="text-xl font-bold text-white">Unsubscribed Users</h3>
                      <p className="text-sm text-[#FDF2F8]/50 mt-1">
                        {unsubscribedUsers.length} user{unsubscribedUsers.length !== 1 ? 's' : ''} have unsubscribed from marketing emails
                      </p>
                    </div>
                    <button 
                      onClick={() => setShowUnsubscribedModal(false)}
                      className="p-2 text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {unsubscribedUsers.length === 0 ? (
                      <div className="text-center py-8 text-[#FDF2F8]/40">
                        <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No users have unsubscribed yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {unsubscribedUsers.map((user) => (
                          <div key={user.user_id} className="flex items-center justify-between bg-[#4A0A66]/20 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-red-400/20 flex items-center justify-center">
                                <Mail className="w-4 h-4 text-red-400" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{user.first_name || user.name || 'User'}</p>
                                <p className="text-sm text-[#FDF2F8]/50">{user.email}</p>
                              </div>
                            </div>
                            {user.unsubscribed_at && (
                              <span className="text-xs text-[#FDF2F8]/40">
                                {format(new Date(user.unsubscribed_at), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="p-6 border-t border-[#4A0A66]/50 bg-[#1a0527]/50">
                    <button
                      onClick={() => setShowUnsubscribedModal(false)}
                      className="w-full py-2 bg-[#4A0A66]/50 hover:bg-[#4A0A66] text-white rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* User Detail Modal */}
            {selectedUser && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
                <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
                  <div className="flex items-center justify-between p-6 border-b border-[#4A0A66]/50">
                    <div className="flex items-center gap-4">
                      {selectedUser.picture ? (
                        <img src={selectedUser.picture} alt="" className="w-14 h-14 rounded-full" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-xl font-bold">
                          {(selectedUser.first_name || selectedUser.name || selectedUser.email || 'U')[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold text-white">{selectedUser.first_name || selectedUser.name || 'User'}</h3>
                          {/* Auth Type Icon */}
                          {selectedUser.auth_type === 'google' || selectedUser.auth_type === 'google_oauth' ? (
                            <span className="bg-blue-500/30 border border-blue-500/50 px-2 py-1 rounded-lg text-xs font-semibold text-blue-300 flex items-center gap-1" title="Signed up with Google">
                              <svg className="w-4 h-4" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                              </svg>
                              Google
                            </span>
                          ) : (
                            <span className="bg-[#df1283]/20 border border-[#df1283]/50 px-2 py-1 rounded-lg text-xs font-semibold text-[#ff69b4] flex items-center gap-1" title="Signed up with Email">
                              <Mail className="w-4 h-4" />
                              Email
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#FDF2F8]/50">{selectedUser.email}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => { setSelectedUser(null); setUserEmailHistory([]); }}
                      className="p-2 text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {loadingUserDetails ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FFFE2]"></div>
                      </div>
                    ) : (
                      <>
                        {/* Auth Method Badge - PROMINENT TOP - Google vs Email - Clickable to change */}
                        <div className="mb-4 p-4 rounded-xl border-2" style={{ 
                          borderColor: selectedUser.auth_type === 'google' || selectedUser.auth_type === 'google_oauth' ? '#4285F4' : '#df1283',
                          backgroundColor: selectedUser.auth_type === 'google' || selectedUser.auth_type === 'google_oauth' ? 'rgba(66, 133, 244, 0.15)' : 'rgba(223, 18, 131, 0.15)'
                        }}>
                          <p className="text-xs text-[#FDF2F8]/60 mb-2 text-center font-medium uppercase tracking-wide">Sign-up Method</p>
                          {selectedUser.auth_type === 'google' || selectedUser.auth_type === 'google_oauth' ? (
                            <button
                              onClick={async () => {
                                if (window.confirm('Change this user from Google Sign-up to Email Sign-up?')) {
                                  try {
                                    const res = await axios.put(`${API_BASE}/api/admin/user/${selectedUser.user_id}/auth-type`, 
                                      { auth_type: 'email' },
                                      { headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }}
                                    );
                                    if (res.data.success) {
                                      toast.success('Auth type updated to Email');
                                      setSelectedUser(prev => ({ ...prev, auth_type: 'email' }));
                                    }
                                  } catch (err) {
                                    toast.error('Failed to update auth type');
                                  }
                                }
                              }}
                              className="w-full px-4 py-3 bg-blue-500/30 text-blue-200 rounded-lg text-base font-bold flex items-center justify-center gap-3 hover:bg-blue-500/40 transition-colors cursor-pointer border border-blue-500/50"
                              title="Click to change to Email sign-up"
                            >
                              <svg className="w-6 h-6" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                              </svg>
                              SIGNED UP WITH GOOGLE
                            </button>
                          ) : (
                            <button
                              onClick={async () => {
                                if (window.confirm('Change this user from Email Sign-up to Google Sign-up?')) {
                                  try {
                                    const res = await axios.put(`${API_BASE}/api/admin/user/${selectedUser.user_id}/auth-type`, 
                                      { auth_type: 'google_oauth' },
                                      { headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }}
                                    );
                                    if (res.data.success) {
                                      toast.success('Auth type updated to Google');
                                      setSelectedUser(prev => ({ ...prev, auth_type: 'google_oauth' }));
                                    }
                                  } catch (err) {
                                    toast.error('Failed to update auth type');
                                  }
                                }
                              }}
                              className="w-full px-4 py-3 bg-[#df1283]/30 text-[#ff69b4] rounded-lg text-base font-bold flex items-center justify-center gap-3 hover:bg-[#df1283]/40 transition-colors cursor-pointer border border-[#df1283]/50"
                              title="Click to change to Google sign-up"
                            >
                              <Mail className="w-6 h-6" />
                              SIGNED UP WITH EMAIL
                            </button>
                          )}
                          <p className="text-[10px] text-[#FDF2F8]/40 mt-2 text-center">Click to change if incorrect</p>
                        </div>

                        {/* User Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                          <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-[#0FFFE2]">{selectedUser.entry_count || 0}</div>
                            <div className="text-xs text-[#FDF2F8]/50">Journal Entries</div>
                          </div>
                          <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-[#FF7901]">{selectedUser.streak || 0}</div>
                            <div className="text-xs text-[#FDF2F8]/50">Day Streak</div>
                          </div>
                          <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-white">{userEmailHistory.filter(e => e.event_type === 'open').length}</div>
                            <div className="text-xs text-[#FDF2F8]/50">Emails Opened</div>
                          </div>
                          <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-white">{userEmailHistory.filter(e => e.event_type === 'click').length}</div>
                            <div className="text-xs text-[#FDF2F8]/50">Links Clicked</div>
                          </div>
                        </div>
                        
                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-2 mb-6">
                          {selectedUser.email_unsubscribed && (
                            <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">Unsubscribed</span>
                          )}
                          {selectedUser.email_bounced && (
                            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium">Email Bounced</span>
                          )}
                          {selectedUser.referral_source && (
                            <span className="px-3 py-1 bg-[#0FFFE2]/20 text-[#0FFFE2] rounded-full text-xs font-medium">
                              Source: {selectedUser.referral_source}
                            </span>
                          )}
                          {selectedUser.created_at && (
                            <span className="px-3 py-1 bg-[#4A0A66]/30 text-[#FDF2F8]/70 rounded-full text-xs">
                              Joined: {format(new Date(selectedUser.created_at), 'MMM d, yyyy')}
                            </span>
                          )}
                          {selectedUser.last_login && (
                            <span className="px-3 py-1 bg-[#4A0A66]/30 text-[#FDF2F8]/70 rounded-full text-xs">
                              Last login: {format(new Date(selectedUser.last_login), 'MMM d, yyyy')}
                            </span>
                          )}
                        </div>

                        {/* Partnership Section - Only for partnership users */}
                        {selectedUser.partnership_id && (
                          <div className="mb-6 p-4 rounded-xl bg-[#FF7901]/10 border border-[#FF7901]/30">
                            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-[#FF7901]" />
                              Partnership Member
                            </h4>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[#FDF2F8]/60">Organization</span>
                                <span className="text-sm text-white font-medium">{selectedUser.partnership_name || 'Unknown'}</span>
                              </div>
                              {selectedUser.partnership_code && (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-[#FDF2F8]/60">Access Code</span>
                                  <span className="text-sm text-[#FF7901] font-mono">{selectedUser.partnership_code}</span>
                                </div>
                              )}
                              {selectedUser.partnership_end_date && (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-[#FDF2F8]/60">Access Expires</span>
                                  <span className="text-sm text-white">{format(new Date(selectedUser.partnership_end_date), 'MMM d, yyyy')}</span>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`${API_BASE}/api/admin/partnerships/resend-welcome-email/${selectedUser.user_id}`, {}, { withCredentials: true });
                                  toast.success(`Partnership welcome email resent to ${selectedUser.email}`);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to resend email');
                                }
                              }}
                              className="mt-4 w-full px-4 py-2 bg-[#FF7901]/20 hover:bg-[#FF7901]/30 text-[#FF7901] rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors border border-[#FF7901]/30"
                            >
                              <Mail className="w-4 h-4" />
                              Resend Partnership Welcome Email
                            </button>
                          </div>
                        )}

                        {/* UTM Traffic Source Tracking - Always visible */}
                        <div className="mb-6" data-testid="user-utm-tracking-section">
                          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#0FFFE2]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="2" y1="12" x2="22" y2="12"/>
                              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                            </svg>
                            Traffic Source (UTM)
                          </h4>
                          {(selectedUser.utm_source || selectedUser.utm_medium || selectedUser.utm_campaign || selectedUser.utm_term || selectedUser.utm_content) ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {selectedUser.utm_source && (
                                <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="utm-source-display">
                                  <div className="text-xs text-[#FDF2F8]/50 mb-1">Source</div>
                                  <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_source}>
                                    {selectedUser.utm_source}
                                  </div>
                                </div>
                              )}
                              {selectedUser.utm_medium && (
                                <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="utm-medium-display">
                                  <div className="text-xs text-[#FDF2F8]/50 mb-1">Medium</div>
                                  <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_medium}>
                                    {selectedUser.utm_medium}
                                  </div>
                                </div>
                              )}
                              {selectedUser.utm_campaign && (
                                <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="utm-campaign-display">
                                  <div className="text-xs text-[#FDF2F8]/50 mb-1">Campaign</div>
                                  <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_campaign}>
                                    {selectedUser.utm_campaign}
                                  </div>
                                </div>
                              )}
                              {selectedUser.utm_term && (
                                <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="utm-term-display">
                                  <div className="text-xs text-[#FDF2F8]/50 mb-1">Term</div>
                                  <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_term}>
                                    {selectedUser.utm_term}
                                  </div>
                                </div>
                              )}
                              {selectedUser.utm_content && (
                                <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="utm-content-display">
                                  <div className="text-xs text-[#FDF2F8]/50 mb-1">Content</div>
                                  <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_content}>
                                    {selectedUser.utm_content}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-[#FDF2F8]/40 text-sm text-center py-3 bg-[#4A0A66]/10 rounded-lg">No UTM tracking data available for this user</p>
                          )}
                        </div>
                        
                        {/* Email Activity History */}
                        <div>
                          <h4 className="text-white font-medium mb-3">Email Activity History</h4>
                          {userEmailHistory.length === 0 ? (
                            <div className="text-center py-6 text-[#FDF2F8]/40">
                              <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                              <p className="text-sm">No email activity recorded</p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {userEmailHistory.map((event, idx) => (
                                <div key={event.id || `email-hist-${event.timestamp || idx}`} className="flex items-start gap-3 bg-[#4A0A66]/10 rounded-lg p-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    event.event_type === 'open' ? 'bg-[#0FFFE2]/20' : 'bg-[#FF7901]/20'
                                  }`}>
                                    {event.event_type === 'open' 
                                      ? <Eye className="w-4 h-4 text-[#0FFFE2]" />
                                      : <MousePointer className="w-4 h-4 text-[#FF7901]" />
                                    }
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-white">
                                      {event.event_type === 'open' ? 'Opened' : 'Clicked'}: <span className="text-[#FDF2F8]/70">{event.campaign_subject}</span>
                                    </p>
                                    {event.url && (
                                      <p className="text-xs text-[#FDF2F8]/40 truncate">{event.url}</p>
                                    )}
                                    <p className="text-xs text-[#FDF2F8]/30 mt-1">
                                      {format(new Date(event.timestamp), 'MMM d, yyyy \'at\' h:mm a')}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="p-6 border-t border-[#4A0A66]/50 bg-[#1a0527]/50">
                    <button
                      onClick={() => { setSelectedUser(null); setUserEmailHistory([]); }}
                      className="w-full py-2 bg-[#4A0A66]/50 hover:bg-[#4A0A66] text-white rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bulk Email Campaign Modal */}
            {showBulkEmailModal && (
              <BulkEmailCampaign
                onClose={() => setShowBulkEmailModal(false)}
                onCampaignSent={() => {
                  // Refresh campaigns list
                  axios.get(`${API_BASE}/api/admin/email/campaigns`)
                    .then(res => setEmailCampaigns(res.data?.campaigns || []))
                    .catch(() => {});
                }}
              />
            )}

            {/* Campaign Analytics Modal */}
            {showCampaignAnalytics && (
              <CampaignAnalytics onClose={() => setShowCampaignAnalytics(false)} />
            )}

            {/* Compose Email Modal - Enhanced Email Marketing Style */}
            {showEmailModal && (
              <div data-testid="email-campaign-modal" className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-6xl mb-8 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]/50 bg-[#1a0527]/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Email Campaign</h3>
                        <p className="text-xs text-[#FDF2F8]/50">Create and preview your email</p>
                      </div>
                    </div>
                    <button onClick={() => { setShowEmailModal(false); setEmailStatus(null); }} className="p-2 text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg transition-colors">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex flex-col lg:flex-row">
                    {/* Left Side - Editor */}
                    <div className="flex-1 p-6 border-r border-[#4A0A66]/30">
                      <div className="space-y-4">
                        {/* Recipients */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                            <Users className="w-4 h-4 text-[#0FFFE2]" />
                            Recipients
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={newEmail.send_to === 'all' ? '' : (users?.find(u => u.user_id === newEmail.send_to)?.email || newEmail.send_to_search || '')}
                              onChange={(e) => {
                                const searchValue = e.target.value;
                                setNewEmail({...newEmail, send_to_search: searchValue, send_to: ''});
                              }}
                              placeholder="Search users or leave empty for all"
                              className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg px-4 py-3 text-white placeholder:text-[#FDF2F8]/30 focus:outline-none focus:border-[#FF7901] focus:ring-1 focus:ring-[#FF7901]/50"
                            />
                            {newEmail.send_to === 'all' && !newEmail.send_to_search && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#0FFFE2]/20 text-[#0FFFE2] text-sm font-medium rounded-full">
                                All Users ({users?.length || 0})
                              </div>
                            )}
                            {newEmail.send_to && newEmail.send_to !== 'all' && (
                              <button 
                                onClick={() => setNewEmail({...newEmail, send_to: 'all', send_to_search: ''})}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FDF2F8]/50 hover:text-white"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {newEmail.send_to_search && newEmail.send_to_search.length > 0 && (
                            <div className="mt-2 bg-[#2D063E] border border-[#4A0A66] rounded-lg max-h-40 overflow-y-auto">
                              {users?.filter(u => 
                                u.email?.toLowerCase().includes(newEmail.send_to_search.toLowerCase()) ||
                                u.first_name?.toLowerCase().includes(newEmail.send_to_search.toLowerCase()) ||
                                u.name?.toLowerCase().includes(newEmail.send_to_search.toLowerCase())
                              ).slice(0, 8).map((user) => (
                                <button
                                  key={user.user_id}
                                  onClick={() => setNewEmail({...newEmail, send_to: user.user_id, send_to_search: ''})}
                                  className="w-full px-4 py-2 text-left text-white hover:bg-[#4A0A66]/50 flex items-center justify-between"
                                >
                                  <span className="truncate">{user.email}</span>
                                  {(user.first_name || user.name) && (
                                    <span className="text-[#FDF2F8]/50 text-sm ml-2">
                                      {user.first_name || user.name?.split(' ')[0]}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Subject */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                            <Type className="w-4 h-4 text-[#FF7901]" />
                            Subject Line
                          </label>
                          <Input
                            data-testid="email-subject-input"
                            value={newEmail.subject}
                            onChange={(e) => setNewEmail({...newEmail, subject: e.target.value})}
                            placeholder="e.g., Exciting news from UnCloned!"
                            className="bg-[#4A0A66]/30 border-[#4A0A66] text-white h-12 text-base focus:border-[#FF7901] focus:ring-1 focus:ring-[#FF7901]/50"
                          />
                        </div>

                        {/* Email Content */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="flex items-center gap-2 text-sm font-medium text-white">
                              <FileText className="w-4 h-4 text-[#df1283]" />
                              Email Content
                            </label>
                            <div className="flex items-center gap-1 bg-[#1a0527] rounded-lg p-1">
                              <button
                                onClick={() => setEditorMode('blocks')}
                                className={`px-3 py-1 text-xs rounded-md transition-colors ${editorMode === 'blocks' ? 'bg-[#FF7901] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                              >
                                Blocks
                              </button>
                              <button
                                onClick={() => setEditorMode('code')}
                                className={`px-3 py-1 text-xs rounded-md transition-colors ${editorMode === 'code' ? 'bg-[#FF7901] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                              >
                                Code
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-[#FDF2F8]/40 mb-2">
                            Use {"{{first_name}}"} or {"{{email}}"} for personalization
                          </p>
                          {editorMode === 'blocks' ? (
                            <EmailBlockBuilder
                              value={newEmail.body_html}
                              onChange={(html) => setNewEmail({...newEmail, body_html: html})}
                            />
                          ) : (
                            <EmailEditor
                              value={newEmail.body_html}
                              onChange={(html) => setNewEmail({...newEmail, body_html: html})}
                              placeholder="Hi {{first_name}}, We have exciting news to share..."
                            />
                          )}
                        </div>

                        {/* Scheduling Options */}
                        <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                          <label className="flex items-center gap-2 text-sm font-medium text-white mb-3">
                            <CalendarClock className="w-4 h-4 text-[#0FFFE2]" />
                            Delivery Options
                          </label>
                          
                          {/* Schedule Type Tabs */}
                          <div className="flex gap-2 mb-4">
                            <button
                              onClick={() => setNewEmail({...newEmail, schedule_type: 'now', scheduled_at: '', recurrence: 'none'})}
                              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                newEmail.schedule_type === 'now' 
                                  ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white' 
                                  : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:text-white'
                              }`}
                            >
                              <Send className="w-3 h-3 inline mr-1" />
                              Immediate
                            </button>
                            <button
                              onClick={() => setNewEmail({...newEmail, schedule_type: 'scheduled', recurrence: 'none'})}
                              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                newEmail.schedule_type === 'scheduled' 
                                  ? 'bg-gradient-to-r from-[#0FFFE2] to-[#0FFFE2]/70 text-black' 
                                  : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:text-white'
                              }`}
                            >
                              <Clock className="w-3 h-3 inline mr-1" />
                              Schedule
                            </button>
                            <button
                              onClick={() => setNewEmail({...newEmail, schedule_type: 'recurring', recurrence: 'weekly'})}
                              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                newEmail.schedule_type === 'recurring' 
                                  ? 'bg-gradient-to-r from-[#FF7901] to-[#FF7901]/70 text-white' 
                                  : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:text-white'
                              }`}
                            >
                              <Repeat className="w-3 h-3 inline mr-1" />
                              Recurring
                            </button>
                          </div>
                          
                          {/* Date/Time Picker (for scheduled and recurring) */}
                          {(newEmail.schedule_type === 'scheduled' || newEmail.schedule_type === 'recurring') && (
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-[#FDF2F8]/50 mb-1 block">
                                  {newEmail.schedule_type === 'recurring' ? 'First Send Date & Time' : 'Send Date & Time'}
                                </label>
                                <input
                                  type="datetime-local"
                                  value={newEmail.scheduled_at}
                                  onChange={(e) => setNewEmail({...newEmail, scheduled_at: e.target.value})}
                                  min={new Date().toISOString().slice(0, 16)}
                                  className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0FFFE2]"
                                />
                              </div>
                              
                              {/* Recurrence Options (for recurring only) */}
                              {newEmail.schedule_type === 'recurring' && (
                                <div>
                                  <label className="text-xs text-[#FDF2F8]/50 mb-1 block">Repeat</label>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => setNewEmail({...newEmail, recurrence: 'weekly'})}
                                      className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${
                                        newEmail.recurrence === 'weekly' 
                                          ? 'bg-[#FF7901] text-white' 
                                          : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:text-white'
                                      }`}
                                    >
                                      Weekly
                                    </button>
                                    <button
                                      onClick={() => setNewEmail({...newEmail, recurrence: 'monthly'})}
                                      className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${
                                        newEmail.recurrence === 'monthly' 
                                          ? 'bg-[#FF7901] text-white' 
                                          : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:text-white'
                                      }`}
                                    >
                                      Monthly
                                    </button>
                                  </div>
                                </div>
                              )}
                              
                              <p className="text-xs text-[#FDF2F8]/40">
                                {newEmail.schedule_type === 'recurring' 
                                  ? `Email will be sent ${newEmail.recurrence} starting from the selected date/time.`
                                  : 'Email will be sent at the selected date and time.'}
                                {emailStats.daily_limit 
                                  ? ` Daily limit: ${emailStats.remaining} remaining.`
                                  : ' No daily limit (Pro plan).'}
                              </p>
                            </div>
                          )}
                        </div>

                        {emailStatus && (
                          <div className={`p-4 rounded-xl flex items-center gap-3 ${emailStatus.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {emailStatus.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                            {emailStatus.message}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                          <Button
                            onClick={() => setShowEmailModal(false)}
                            variant="outline"
                            className="flex-1 border-[#4A0A66] text-[#FDF2F8]/70 h-12"
                            disabled={sendingEmail}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSendEmail}
                            className={`flex-1 h-12 text-base font-semibold ${
                              newEmail.schedule_type === 'now' 
                                ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901]' 
                                : newEmail.schedule_type === 'recurring'
                                  ? 'bg-gradient-to-r from-[#FF7901] to-[#df1283]'
                                  : 'bg-gradient-to-r from-[#0FFFE2] to-[#0FFFE2]/70 text-black'
                            }`}
                            disabled={sendingEmail || !newEmail.subject || !newEmail.body_html || ((newEmail.schedule_type === 'scheduled' || newEmail.schedule_type === 'recurring') && !newEmail.scheduled_at)}
                          >
                            {sendingEmail ? (
                              <>
                                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                {newEmail.schedule_type === 'now' ? 'Sending...' : 'Scheduling...'}
                              </>
                            ) : newEmail.schedule_type === 'now' ? (
                              <>
                                <Send className="w-5 h-5 mr-2" />
                                Send Now
                              </>
                            ) : newEmail.schedule_type === 'recurring' ? (
                              <>
                                <Repeat className="w-5 h-5 mr-2" />
                                Schedule Recurring
                              </>
                            ) : (
                              <>
                                <CalendarClock className="w-5 h-5 mr-2" />
                                Schedule Email
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Right Side - Live Preview */}
                    <div className="w-full lg:w-[450px] bg-gray-100 p-4 overflow-y-auto max-h-[80vh]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Live Preview</span>
                        {/* Device Toggle */}
                        <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm">
                          <button
                            onClick={() => setPreviewDevice('mobile')}
                            className={`p-1.5 rounded transition-colors ${previewDevice === 'mobile' ? 'bg-[#df1283] text-white' : 'text-gray-400 hover:text-gray-600'}`}
                            title="Mobile"
                          >
                            <Smartphone className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setPreviewDevice('tablet')}
                            className={`p-1.5 rounded transition-colors ${previewDevice === 'tablet' ? 'bg-[#df1283] text-white' : 'text-gray-400 hover:text-gray-600'}`}
                            title="Tablet"
                          >
                            <Tablet className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setPreviewDevice('desktop')}
                            className={`p-1.5 rounded transition-colors ${previewDevice === 'desktop' ? 'bg-[#df1283] text-white' : 'text-gray-400 hover:text-gray-600'}`}
                            title="Desktop"
                          >
                            <Monitor className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Device Frame */}
                      <div className={`mx-auto transition-all duration-300 ${
                        previewDevice === 'mobile' ? 'max-w-[320px]' : 
                        previewDevice === 'tablet' ? 'max-w-[400px]' : 
                        'max-w-full'
                      }`}>
                        {/* Phone/Tablet Frame for mobile/tablet - Shows actual email wrapper */}
                        {previewDevice !== 'desktop' && (
                          <div className="bg-gray-800 rounded-[2rem] p-2 shadow-xl">
                            <div className="bg-gray-800 rounded-[1.5rem] overflow-hidden">
                              {/* Notch for mobile */}
                              {previewDevice === 'mobile' && (
                                <div className="h-6 bg-gray-800 flex items-center justify-center">
                                  <div className="w-20 h-4 bg-black rounded-full"></div>
                                </div>
                              )}
                              
                              {/* Email Preview - Actual wrapper */}
                              <div className="bg-white overflow-hidden" style={{ backgroundColor: '#f5f5f5' }}>
                                {/* Header with logo - centered */}
                                <div style={{ 
                                  background: 'linear-gradient(135deg, #1a0527 0%, #2D063E 100%)', 
                                  padding: previewDevice === 'mobile' ? '12px' : '16px', 
                                  textAlign: 'center' 
                                }}>
                                  <img 
                                    src="https://customer-assets-jt897jd0.emergentagent.net/job_daily-notes-84/artifacts/7qbqgk18_UnCloned%C2%AE%20Journal-Color%20Logo.png" 
                                    alt="UnCloned® Journal" 
                                    style={{ maxWidth: previewDevice === 'mobile' ? '100px' : '120px', height: 'auto', display: 'inline-block' }} 
                                  />
                                </div>
                                
                                {/* Accent bar */}
                                <div style={{ height: '3px', background: 'linear-gradient(90deg, #df1283, #FF7901)' }}></div>
                                
                                {/* Subject Preview */}
                                <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                                  <p style={{ margin: 0, fontSize: '10px', color: '#999', marginBottom: '2px' }}>Subject:</p>
                                  <p style={{ margin: 0, fontSize: previewDevice === 'mobile' ? '11px' : '12px', fontWeight: '600', color: '#333' }}>
                                    {newEmail.subject || 'Your subject line...'}
                                  </p>
                                </div>
                                
                                {/* Body Preview */}
                                <div className="p-3 max-h-[300px] overflow-y-auto bg-white">
                                  {newEmail.body_html ? (
                                    <div 
                                      className="email-preview-content"
                                      style={{ fontSize: previewDevice === 'mobile' ? '11px' : '12px', lineHeight: '1.5', color: '#333' }}
                                      dangerouslySetInnerHTML={{ 
                                        __html: DOMPurify.sanitize((newEmail.body_html || '')
                                          .replace(/<!-- BLOCKS:.*? -->\n?/g, '')
                                          .replace(/\{\{first_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">John</span>')
                                          .replace(/\{\{email\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">john@example.com</span>'))
                                      }}
                                    />
                                  ) : (
                                    <p className="text-gray-400 italic text-xs">Your email content...</p>
                                  )}
                                </div>
                                
                                {/* Footer */}
                                <div style={{ 
                                  backgroundColor: '#fafafa', 
                                  padding: '10px 12px', 
                                  textAlign: 'center', 
                                  borderTop: '1px solid #eee' 
                                }}>
                                  <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '10px' }}>
                                    Sent with 💜 from <span style={{ color: '#df1283', fontWeight: '600' }}>UnCloned®</span>
                                  </p>
                                  <p style={{ margin: 0, color: '#999', fontSize: '9px' }}>
                                    © 2026 UnCloned® Journal
                                  </p>
                                </div>
                              </div>
                              
                              {/* Home indicator for mobile */}
                              {previewDevice === 'mobile' && (
                                <div className="h-5 bg-gray-800 flex items-center justify-center">
                                  <div className="w-24 h-1 bg-white rounded-full"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Desktop Preview - Shows actual email wrapper */}
                        {previewDevice === 'desktop' && (
                          <div className="rounded-xl overflow-hidden shadow-lg" style={{ backgroundColor: '#f5f5f5' }}>
                            {/* Email Container - Mimics actual email */}
                            <div style={{ 
                              maxWidth: '100%', 
                              backgroundColor: '#ffffff', 
                              borderRadius: '12px', 
                              overflow: 'hidden',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              margin: '8px'
                            }}>
                              {/* Header with logo - centered */}
                              <div style={{ 
                                background: 'linear-gradient(135deg, #1a0527 0%, #2D063E 100%)', 
                                padding: '20px', 
                                textAlign: 'center' 
                              }}>
                                <img 
                                  src="https://customer-assets-jt897jd0.emergentagent.net/job_daily-notes-84/artifacts/7qbqgk18_UnCloned%C2%AE%20Journal-Color%20Logo.png" 
                                  alt="UnCloned® Journal" 
                                  style={{ maxWidth: '160px', height: 'auto', display: 'inline-block' }} 
                                />
                              </div>
                              
                              {/* Accent bar */}
                              <div style={{ height: '4px', background: 'linear-gradient(90deg, #df1283, #FF7901)' }}></div>
                              
                              {/* Subject line preview */}
                              <div style={{ padding: '12px 20px', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                                <p style={{ margin: 0, fontSize: '12px', color: '#999', marginBottom: '4px' }}>Subject:</p>
                                <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#333' }}>
                                  {newEmail.subject || 'Your subject line here...'}
                                </p>
                              </div>
                              
                              {/* Main content */}
                              <div style={{ padding: '20px', color: '#333333', fontSize: '14px', lineHeight: '1.6', minHeight: '120px' }}>
                                {newEmail.body_html ? (
                                  <div 
                                    className="email-preview-content"
                                    dangerouslySetInnerHTML={{ 
                                      __html: DOMPurify.sanitize(`
                                        <style>
                                          .email-preview-content p { margin: 0 0 1em 0; }
                                          .email-preview-content p:last-child { margin-bottom: 0; }
                                          .email-preview-content br { display: block; content: ""; margin-top: 0.5em; }
                                          .email-preview-content h1 { font-size: 1.5em; font-weight: bold; margin: 0.5em 0; color: #df1283; }
                                          .email-preview-content h2 { font-size: 1.25em; font-weight: bold; margin: 0.5em 0; color: #333; }
                                          .email-preview-content ul, .email-preview-content ol { padding-left: 1.5em; margin: 0.5em 0; }
                                          .email-preview-content li { margin: 0.25em 0; }
                                          .email-preview-content blockquote { border-left: 3px solid #df1283; padding-left: 1em; margin: 0.5em 0; color: #666; font-style: italic; }
                                          .email-preview-content a { color: #0FFFE2; }
                                          .email-preview-content img { max-width: 100%; height: auto; border-radius: 8px; }
                                        </style>
                                      ` + (newEmail.body_html || '')
                                        .replace(/<!-- BLOCKS:.*? -->\n?/g, '')
                                        .replace(/\{\{first_name\}\}/g, '<span style="background:#fef3c7;padding:0 4px;border-radius:4px;">John</span>')
                                        .replace(/\{\{email\}\}/g, '<span style="background:#fef3c7;padding:0 4px;border-radius:4px;">john@example.com</span>'))
                                    }}
                                  />
                                ) : (
                                  <p style={{ color: '#999', fontStyle: 'italic' }}>Your email content will appear here...</p>
                                )}
                              </div>
                              
                              {/* Footer */}
                              <div style={{ 
                                backgroundColor: '#fafafa', 
                                padding: '16px 20px', 
                                textAlign: 'center', 
                                borderTop: '1px solid #eeeeee' 
                              }}>
                                <p style={{ margin: '0 0 8px 0', color: '#666666', fontSize: '13px' }}>
                                  Sent with 💜 from <span style={{ color: '#df1283', fontWeight: '600' }}>UnCloned® Journal</span>
                                </p>
                                <p style={{ margin: 0, color: '#999999', fontSize: '11px' }}>
                                  © 2026 UnCloned® Journal. All rights reserved.
                                </p>
                                <p style={{ margin: '10px 0 0 0' }}>
                                  <span style={{ color: '#999999', textDecoration: 'underline', fontSize: '11px' }}>
                                    Unsubscribe from these emails
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Preview Info */}
                      <div className="mt-3 p-3 bg-white/50 rounded-lg">
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">
                            {previewDevice === 'mobile' ? '📱 Mobile' : previewDevice === 'tablet' ? '📱 Tablet' : '🖥️ Desktop'} Preview
                          </span>
                          {' • '}Personalization tags will be replaced with actual user data.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'inactive-accounts' ? (
          <InactiveAccountsTab />
        ) : activeTab === 'clients' ? (
          <ClientsTab
            clients={clients}
            filteredClients={filteredClients}
            selectedClient={selectedClient}
            clientsTotals={clientsTotals}
            clientSearchQuery={clientSearchQuery}
            loadingClients={loadingClients}
            showBulkUploadModal={showBulkUploadModal}
            showAddUserModal={showAddUserModal}
            bulkUploadData={bulkUploadData}
            setClientSearchQuery={setClientSearchQuery}
            setSelectedClient={setSelectedClient}
            setShowBulkUploadModal={setShowBulkUploadModal}
            setShowAddUserModal={setShowAddUserModal}
            setBulkUploadData={setBulkUploadData}
            handleViewClient={handleViewClient}
            handleResendInvite={handleResendInvite}
            handleInviteUser={handleInviteUser}
            handleBulkInvite={handleBulkInvite}
            handleCSVUpload={handleCSVUpload}
          />
        ) : activeTab === 'automation' ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#0FFFE2]" />
                Email Automations & Workflows
              </h2>
            </div>
            
            <p className="text-[#FDF2F8]/70 text-sm">
              Configure automated emails that are sent to users based on triggers like signup, inactivity, streak milestones, and more.
            </p>
            
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'all', label: 'All Automations', icon: '📧' },
                { id: 'welcome', label: 'Welcome Series', icon: '👋' },
                { id: 'reengagement', label: 'Re-engagement', icon: '🔄' },
                { id: 'streak', label: 'Streak Rewards', icon: '🔥' },
                { id: 'digest', label: 'Digests', icon: '📊' },
                { id: 'feature', label: 'Feature Discovery', icon: '✨' }
              ].map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedAutomationCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedAutomationCategory === cat.id
                      ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white'
                      : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:bg-[#4A0A66]/50'
                  }`}
                >
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>
            
            {/* Available Variables Card */}
            <div className="bg-[#4A0A66]/20 rounded-xl border border-[#4A0A66]/50 p-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Type className="w-4 h-4 text-[#FF7901]" />
                Available Email Variables
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(emailVariables).map(([category, vars]) => (
                  <div key={category} className="bg-[#2D063E]/50 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-[#0FFFE2] uppercase tracking-wider mb-2">
                      {category}
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {Object.keys(vars).map(varName => (
                        <span
                          key={varName}
                          className="px-2 py-0.5 bg-[#FF7901]/20 text-[#FF7901] rounded text-xs cursor-pointer hover:bg-[#FF7901]/30"
                          title={vars[varName]}
                          onClick={() => navigator.clipboard.writeText(`{{${varName}}}`).then(() => toast.success(`Copied {{${varName}}}`))}
                        >
                          {`{{${varName}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#FDF2F8]/40 mt-3">
                Click any variable to copy it to clipboard
              </p>
            </div>

            {/* Automations Grid */}
            <div className="grid gap-4">
              {automations
                .filter(auto => {
                  if (selectedAutomationCategory === 'all') return true;
                  const type = auto.automation_type;
                  if (selectedAutomationCategory === 'welcome') return type.startsWith('welcome');
                  if (selectedAutomationCategory === 'reengagement') return type.startsWith('reengagement') || type === 'streak_broken';
                  if (selectedAutomationCategory === 'streak') return type.startsWith('streak_milestone');
                  if (selectedAutomationCategory === 'digest') return type.includes('digest') || type.includes('recap');
                  if (selectedAutomationCategory === 'feature') return type.startsWith('feature_') || type === 'first_entry' || type === 'badge_earned';
                  return true;
                })
                .map((automation) => (
                <div 
                  key={automation.automation_type}
                  className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl border border-[#4A0A66] p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${automation.enabled ? 'bg-[#0FFFE2]/20' : 'bg-[#4A0A66]/50'}`}>
                        <Mail className={`w-5 h-5 ${automation.enabled ? 'text-[#0FFFE2]' : 'text-[#FDF2F8]/50'}`} />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">
                          {automation.trigger_info?.name || automation.automation_type.replace(/_/g, ' ')}
                        </h3>
                        <p className="text-sm text-[#FDF2F8]/50">
                          {automation.trigger_info?.description || 'Configure this automation'}
                        </p>
                        {/* Show delay days if applicable */}
                        {automation.trigger_info?.default_delay_days && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-[#FDF2F8]/40">Delay:</span>
                            <select
                              value={automation.delay_days || automation.trigger_info.default_delay_days}
                              onChange={(e) => handleUpdateAutomation(automation.automation_type, {
                                delay_days: parseInt(e.target.value)
                              })}
                              className="bg-[#4A0A66]/50 border border-[#4A0A66] text-white text-xs px-2 py-0.5 rounded"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {[1, 2, 3, 5, 7, 10, 14, 21, 30, 45, 60, 90].map(d => (
                                <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        automation.enabled 
                          ? 'bg-[#22c55e]/20 text-[#22c55e]' 
                          : 'bg-[#ef4444]/20 text-[#ef4444]'
                      }`}>
                        {automation.enabled ? 'Active' : 'Disabled'}
                      </span>
                      {automation.subject && (
                        <Button
                          onClick={() => window.open(`/api/admin/automations/${automation.automation_type}/preview?name=Sarah`, '_blank')}
                          variant="ghost"
                          size="sm"
                          className="text-[#FDF2F8]/70 hover:text-white"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setEditingTemplate({
                            ...automation,
                            template_type: automation.automation_type
                          });
                          setShowTemplateModal(true);
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-[#0FFFE2] hover:text-white"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {automation.subject ? (
                    <div className="bg-[#4A0A66]/30 rounded-lg p-4">
                      <div className="text-sm text-[#FDF2F8]/50 mb-1">Subject Line:</div>
                      <div className="text-white font-medium">{automation.subject}</div>
                    </div>
                  ) : (
                    <div className="bg-[#4A0A66]/30 rounded-lg p-4 text-center">
                      <div className="text-sm text-[#FDF2F8]/50">Not configured yet</div>
                      <Button
                        onClick={() => {
                          setEditingTemplate({
                            automation_type: automation.automation_type,
                            template_type: automation.automation_type,
                            subject: '',
                            body_html: '',
                            enabled: false
                          });
                          setShowTemplateModal(true);
                        }}
                        size="sm"
                        className="mt-2 bg-gradient-to-r from-[#df1283] to-[#FF7901]"
                      >
                        Set Up Now
                      </Button>
                    </div>
                  )}
                  
                  {/* Stats */}
                  {automation.stats && (automation.stats.sent > 0 || automation.stats.opened > 0) && (
                    <div className="mt-3 flex items-center gap-4 text-xs text-[#FDF2F8]/50">
                      <span>Sent: <span className="text-white">{automation.stats.sent}</span></span>
                      <span>Opened: <span className="text-[#0FFFE2]">{automation.stats.opened}</span></span>
                      <span>Clicked: <span className="text-[#FF7901]">{automation.stats.clicked}</span></span>
                    </div>
                  )}
                </div>
              ))}
              
              {automations.length === 0 && (
                <div className="text-center py-12 text-[#FDF2F8]/50">
                  Loading automations...
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'promos' ? (
          <PromoCampaignManager />
        ) : activeTab === 'email-templates' ? (
          <SystemEmailTemplates />
        ) : activeTab === 'email-analytics' ? (
          <EmailAnalyticsDashboard />
        ) : activeTab === 'fb-pixel' ? (
          <FacebookPixelManager />
        ) : activeTab === 'subscriptions' ? (
          <SubscriptionManager users={users} onRefresh={fetchData} />
        ) : activeTab === 'docs' ? (
          <DocumentationHub />
        ) : activeTab === 'referral' ? (
          <ReferralManagement />
        ) : activeTab === 'partnerships' ? (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Organization Partnerships</h2>
              <p className="text-[#FDF2F8]/60">Manage partnerships with non-profits, schools, and organizations. Generate bulk access codes for partner members.</p>
            </div>
            <PartnershipsManager />
          </div>
        ) : activeTab === 'investor-tools' ? (
          <InvestorToolsTab />
        ) : activeTab === 'data-cleanup' ? (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Profile Data Cleanup</h2>
              <p className="text-[#FDF2F8]/60">Find and fix users with incomplete profile data (missing name, phone, etc.)</p>
            </div>
            <DataCleanupManager />
          </div>
        ) : activeTab === 'entry-engagement' ? (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Entry Engagement</h2>
              <p className="text-[#FDF2F8]/60">Nudge users who signed up but haven't created their first journal entry yet</p>
            </div>
            <EntryEngagementManager />
          </div>
        ) : activeTab === 'settings' ? (
          <div className="space-y-8">
            {/* Multiple Video Embeds Manager - Using Unified Component */}
            <CollapsibleSection 
              id="video-embeds" 
              title="Video Embeds" 
              icon={Video}
              iconColor="#FF7901"
              defaultOpen={true}
            >
              <VideoEmbedsManagerUnified 
                isAdmin={true}
                showTierSelector={true}
                showAnalytics={true}
              />
            </CollapsibleSection>
            
            {/* Legacy Display Settings (for carousel and sidebar toggles) */}
            <CollapsibleSection 
              id="display-toggles" 
              title="Display Toggles" 
              icon={Settings2}
              iconColor="#10B981"
              defaultOpen={true}
            >
              <DashboardDisplaySettings />
            </CollapsibleSection>
            
            {/* Signup Settings - Default tier for new users */}
            <CollapsibleSection 
              id="signup-settings" 
              title="Signup Settings" 
              icon={UserPlus}
              iconColor="#0FFFE2"
              defaultOpen={false}
            >
              <SignupSettings />
            </CollapsibleSection>
            
            {/* Full Tier Editor - Labels, Features, Badges, Pricing */}
            <CollapsibleSection 
              id="tier-editor" 
              title="Tier Editor" 
              icon={Crown}
              iconColor="#df1283"
              defaultOpen={false}
            >
              <TierEditor />
            </CollapsibleSection>
            
            <CollapsibleSection 
              id="referral-sources" 
              title="Referral Sources" 
              icon={Link2}
              iconColor="#0FFFE2"
              defaultOpen={false}
            >
              <ReferralSourcesSettings />
            </CollapsibleSection>
            
            <CollapsibleSection 
              id="onboarding-management" 
              title="Onboarding Management" 
              icon={Sparkles}
              iconColor="#FF7901"
              defaultOpen={false}
            >
              <OnboardingManagement />
            </CollapsibleSection>
            
            {/* Landing Page Settings */}
            <CollapsibleSection 
              id="landing-page-settings" 
              title="Landing Page" 
              icon={Globe}
              iconColor="#0FFFE2"
              defaultOpen={false}
            >
              <LandingPageSettings />
            </CollapsibleSection>
            
            {/* Note about banner management */}
            <div className="p-4 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg">
              <p className="text-sm text-[#FDF2F8]/70">
                <strong className="text-[#FF7901]">Banner Management:</strong> All promotional banners, 
                upgrade CTAs, and rotating messages are now managed in the <strong>Promos</strong> tab. 
                Use "Display Settings" there to configure rotation timing and navigation options.
              </p>
            </div>
            
            {/* Data Tools Section */}
            <CollapsibleSection 
              id="data-tools" 
              title="Data Tools" 
              icon={Wrench}
              iconColor="#FF7901"
              defaultOpen={false}
            >
              <div className="space-y-4">
                <p className="text-sm text-[#FDF2F8]/60">
                  One-time fixes for user data. These operations are safe to run multiple times.
                </p>
                
                {/* Fix Google Auth Types */}
                <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Fix Google Sign-up Labels (Bulk)
                      </h4>
                      <p className="text-xs text-[#FDF2F8]/50 mt-1">
                        Scans all users and marks those with Google profile pictures as Google sign-ups.
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API_BASE}/api/admin/fix-google-auth-types`, {}, {
                            headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
                          });
                          if (res.data.success) {
                            toast.success(res.data.message);
                          } else {
                            toast.error(res.data.error || 'Failed to fix auth types');
                          }
                        } catch (err) {
                          toast.error('Failed to fix Google auth types');
                        }
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      Run Bulk Fix
                    </Button>
                  </div>
                </div>

                {/* Fix Specific User by Email */}
                <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg">
                  <h4 className="text-white font-medium flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Fix Specific User (by Email)
                  </h4>
                  <p className="text-xs text-[#FDF2F8]/50 mb-3">
                    Use this when bulk fix doesn't work. Enter the user's email to manually mark them as a Google sign-up.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      id="fix-user-email-input"
                      placeholder="Enter user email..."
                      className="flex-1 bg-[#2D063E] border border-[#4A0A66] text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#0FFFE2]"
                    />
                    <Button
                      onClick={async () => {
                        const emailInput = document.getElementById('fix-user-email-input');
                        const email = emailInput?.value?.trim();
                        if (!email) {
                          toast.error('Please enter an email address');
                          return;
                        }
                        try {
                          const res = await axios.post(`${API_BASE}/api/admin/fix-user-auth-by-email`, 
                            { email, auth_type: 'google_oauth' },
                            { headers: { 'X-Admin-Token': localStorage.getItem('adminToken') } }
                          );
                          if (res.data.success) {
                            toast.success(`✓ ${res.data.message}`);
                            emailInput.value = '';
                          } else {
                            toast.error(res.data.error || 'Failed to update user');
                          }
                        } catch (err) {
                          const errorMsg = err.response?.data?.detail || 'Failed to update user';
                          toast.error(errorMsg);
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
                    >
                      Mark as Google
                    </Button>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
            
            {/* Tier Management */}
            <TierManager />
            
            {/* Feature Preview Mode - See what each tier can access */}
            <TierPreview />
          </div>
        ) : activeTab === 'security' ? (
          <SecurityManager />
        ) : activeTab === 'overview' && analytics && (
          <>
            {/* Date Range Selector with Custom Dates */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#FF7901]" />
                Dashboard Overview
                {loadingAnalytics && (
                  <span className="ml-2 animate-spin">
                    <RefreshCw className="w-4 h-4 text-[#0FFFE2]" />
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#FDF2F8]/50">View:</span>
                  <select
                    data-testid="admin-overview-date-range"
                    value={analyticsDateRange}
                    onChange={(e) => handleAnalyticsDateRangeChange(e.target.value)}
                    className="bg-[#4A0A66]/50 text-white text-sm px-3 py-1.5 rounded-lg border border-[#4A0A66] focus:outline-none focus:border-[#FF7901] cursor-pointer"
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="7days">Last 7 Days</option>
                    <option value="30days">Last 30 Days</option>
                    <option value="all">All Time</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
                
                {analyticsDateRange === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="bg-[#4A0A66]/50 text-white text-sm px-3 py-1.5 rounded-lg border border-[#4A0A66] focus:outline-none focus:border-[#FF7901] [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:brightness-100"
                    />
                    <span className="text-[#FDF2F8]/50">to</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="bg-[#4A0A66]/50 text-white text-sm px-3 py-1.5 rounded-lg border border-[#4A0A66] focus:outline-none focus:border-[#FF7901] [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:brightness-100"
                    />
                    <Button
                      onClick={applyCustomDateRange}
                      size="sm"
                      className="bg-[#0FFFE2] text-black hover:bg-[#0FFFE2]/80"
                    >
                      Apply
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Date Range Info Banner */}
            {analytics.filter_start && (
              <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/50 rounded-lg px-4 py-2 mb-4 flex items-center justify-between">
                <span className="text-sm text-[#FDF2F8]/70">
                  {analyticsDateRange === 'today' ? (
                    <>Showing data for <span className="text-[#0FFFE2] font-medium">Today ({formatLocalDate(analytics.filter_start)})</span></>
                  ) : analyticsDateRange === 'yesterday' ? (
                    <>Showing data for <span className="text-[#0FFFE2] font-medium">Yesterday ({formatLocalDate(analytics.filter_start)})</span></>
                  ) : (
                    <>Showing data from <span className="text-[#0FFFE2] font-medium">{formatLocalDate(analytics.filter_start)}</span> to <span className="text-[#0FFFE2] font-medium">{formatLocalDate(analytics.filter_end)}</span></>
                  )}
                </span>
              </div>
            )}

            {/* Key Metrics - Collapsible */}
            <CollapsibleSection
              id="key-metrics"
              title="Key Metrics"
              icon={BarChart3}
              iconColor="#22C55E"
              defaultOpen={true}
            >
              {/* Key Metrics - Row 1 (Filtered Data) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <MetricCard 
                  icon={UserPlus} 
                  label={`New Users (${analyticsDateRange === 'today' ? 'Today' : analyticsDateRange === 'yesterday' ? 'Yesterday' : analyticsDateRange === '7days' ? '7d' : analyticsDateRange === '30days' ? '30d' : 'Range'})`}
                  value={analytics.new_users_in_range || 0}
                  changeLabel={`${analytics.total_users} total users`}
                  color="#22C55E"
                  clickable={true}
                  onClick={() => setShowDailyBreakdownModal(true)}
                />
                <MetricCard 
                  icon={BookOpen} 
                  label={`Entries (${analyticsDateRange === 'today' ? 'Today' : analyticsDateRange === 'yesterday' ? 'Yesterday' : analyticsDateRange === '7days' ? '7d' : analyticsDateRange === '30days' ? '30d' : 'Range'})`}
                  value={analytics.entries_in_range || 0}
                  changeLabel={`${analytics.total_entries} total entries`}
                  color="#FF7901"
                  clickable={true}
                  onClick={() => setShowEntriesBreakdownModal(true)}
                />
                <MetricCard 
                  icon={LogIn} 
                  label={`Logins (${analyticsDateRange === 'today' ? 'Today' : analyticsDateRange === 'yesterday' ? 'Yesterday' : analyticsDateRange === '7days' ? '7d' : analyticsDateRange === '30days' ? '30d' : 'Range'})`}
                  value={analytics.logins_in_range || 0}
                  changeLabel={`${analytics.total_logins_this_week || 0} this week`}
                  color="#8B5CF6"
                  clickable={true}
                  onClick={() => setShowLoginsBreakdownModal(true)}
                />
                <MetricCard 
                  icon={Users} 
                  label="Total Users (All Time)" 
                  value={analytics.total_users}
                  changeLabel={`+${analytics.new_users_today || 0} today`}
                  color="#df1283"
                />
              </div>

              {/* Key Metrics - Row 2 (Always show current stats) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard 
                  icon={TrendingUp} 
                  label="Entries Today" 
                  value={analytics.entries_today}
                  color="#22C55E"
                />
                <MetricCard 
                  icon={Calendar} 
                  label="Entries This Week" 
                  value={analytics.entries_this_week}
                  color="#06B6D4"
                />
                <MetricCard 
                  icon={Trophy} 
                  label="Badges Earned" 
                  value={analytics.total_badges || 0}
                  color="#F59E0B"
                />
                <MetricCard 
                  icon={Flame} 
                  label="Active Streaks" 
                  value={analytics.streak_leaders?.length || 0}
                  color="#EF4444"
                />
              </div>
              
              {/* Entry Engagement Card - Shows users with entries vs without */}
              <div className="mt-4 bg-gradient-to-br from-[#2D063E]/50 to-[#1a0527]/50 border border-[#4A0A66]/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#0FFFE2]" />
                    <span className="text-sm font-medium text-white">Entry Engagement</span>
                  </div>
                  <button
                    onClick={() => setActiveTab('entry-engagement')}
                    className="text-xs text-[#df1283] hover:text-[#ff1493] transition-colors"
                  >
                    Manage →
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => setActiveTab('accounts')}
                    className="text-center hover:bg-[#4A0A66]/30 rounded-lg p-2 transition-colors group"
                    title="View all users"
                  >
                    <p className="text-2xl font-bold text-white group-hover:text-[#df1283] transition-colors">{analytics.total_users || 0}</p>
                    <p className="text-xs text-[#FDF2F8]/50">Total Users</p>
                  </button>
                  <button
                    onClick={() => setActiveTab('entry-engagement')}
                    className="text-center hover:bg-[#4A0A66]/30 rounded-lg p-2 transition-colors group"
                    title="View users with entries"
                  >
                    <p className="text-2xl font-bold text-[#22C55E] group-hover:text-[#4ADE80] transition-colors">{analytics.users_with_entries || 0}</p>
                    <p className="text-xs text-[#FDF2F8]/50">Created Entries</p>
                  </button>
                  <button
                    onClick={() => setActiveTab('entry-engagement')}
                    className="text-center hover:bg-[#4A0A66]/30 rounded-lg p-2 transition-colors group"
                    title="View users without entries - Send nudge emails"
                  >
                    <p className="text-2xl font-bold text-[#F59E0B] group-hover:text-[#FBBF24] transition-colors">{(analytics.total_users || 0) - (analytics.users_with_entries || 0)}</p>
                    <p className="text-xs text-[#FDF2F8]/50">No Entries Yet</p>
                  </button>
                </div>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#FDF2F8]/50">Activation Rate</span>
                    <span className="text-[#22C55E]">{analytics.activation_rate || '0%'}</span>
                  </div>
                  <div className="w-full h-2 bg-[#4A0A66]/30 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#df1283] to-[#22C55E] rounded-full transition-all duration-500"
                      style={{ width: analytics.activation_rate || '0%' }}
                    />
                  </div>
                  {((analytics.total_users || 0) - (analytics.users_with_entries || 0)) > 0 && (
                    <p className="text-xs text-[#F59E0B]/70 mt-2">
                      💡 {(analytics.total_users || 0) - (analytics.users_with_entries || 0)} users need a nudge to create their first entry
                    </p>
                  )}
                </div>
              </div>
            </CollapsibleSection>

            {/* Live User Journey Funnel */}
            <div className="mb-6">
              <CollapsibleSection
                id="live-funnel"
                title="Live User Journey"
                icon={Activity}
                iconColor="#0FFFE2"
                defaultOpen={true}
              >
                <LiveFunnelWidget />
              </CollapsibleSection>
            </div>

            {/* Feature Adoption + Charts Row - Collapsible */}
            <div className="mb-6">
              <CollapsibleSection
                id="feature-adoption"
                title="Feature Adoption & Trends"
                icon={Sparkles}
                iconColor="#FF7901"
                defaultOpen={!collapsedSections.featureAdoption}
              >
                <div className="grid md:grid-cols-3 gap-4">
                  <FeatureAdoptionCard 
                    voiceUses={analytics.total_transcriptions || 0}
                    voiceUsers={analytics.voice_users || 0}
                    aiUses={analytics.ai_reflections_count || 0}
                    aiUsers={analytics.ai_users_count || 0}
                    totalUsers={analytics.total_users}
                    onVoiceClick={() => setShowVoiceModal(true)}
                    onAIClick={() => setShowAIModal(true)}
                  />
                  <MiniChart 
                    data={analytics.daily_entries || []} 
                    label="Daily Entries (7d)" 
                    color="#FF7901" 
                  />
                  <MiniChart 
                    data={analytics.daily_signups || []} 
                    label="User Growth (7d)" 
                    color="#22C55E" 
                  />
                </div>
              </CollapsibleSection>
            </div>
            
            {/* Recent Signups Widget */}
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl overflow-hidden mb-6">
              <div className="p-6 pb-4 flex items-center justify-between">
                <button 
                  onClick={() => toggleSection('recentSignups')}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  {collapsedSections.recentSignups ? (
                    <ChevronRight className="w-5 h-5 text-[#FDF2F8]/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#FDF2F8]/60" />
                  )}
                  <Bell className="w-5 h-5 text-[#0FFFE2]" />
                  <h3 className="text-lg font-semibold text-white">Recent Signups</h3>
                  {unseenSignupCount > 0 && (
                    <span className="px-2 py-0.5 bg-[#df1283] text-white text-xs rounded-full animate-pulse">
                      {unseenSignupCount} new
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-2">
                  {/* Sound Picker Button */}
                  <button
                    onClick={() => setShowSoundPicker(!showSoundPicker)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-[#FF7901]/20 text-[#FF7901] rounded-lg hover:bg-[#FF7901]/30 transition-colors text-sm"
                    title="Choose notification sound"
                  >
                    <Music className="w-4 h-4" />
                    {PRESET_SOUNDS[signupAlertSettings.selected_sound]?.icon || '🎵'}
                  </button>
                  
                  {/* Sound Toggle */}
                  <button
                    onClick={() => updateSignupAlertSettings({ sound_enabled: !signupAlertSettings.sound_enabled })}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                      signupAlertSettings.sound_enabled 
                        ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]' 
                        : 'bg-white/10 text-white/40'
                    }`}
                    title={signupAlertSettings.sound_enabled ? 'Sound ON' : 'Sound OFF'}
                  >
                    {signupAlertSettings.sound_enabled ? (
                      <><Volume2 className="w-4 h-4" /> On</>
                    ) : (
                      <><VolumeX className="w-4 h-4" /> Off</>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Collapsible Content */}
              {!collapsedSections.recentSignups && (
              <div className="px-6 pb-6">
              {/* Sound Picker Dropdown */}
              {showSoundPicker && (
                <div className="mb-4 p-4 bg-[#4A0A66]/30 rounded-xl border border-[#4A0A66]">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <Music className="w-4 h-4 text-[#FF7901]" />
                      Choose Notification Sound
                    </h4>
                    <button
                      onClick={() => setShowSoundPicker(false)}
                      className="text-white/40 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                    {Object.entries(PRESET_SOUNDS).map(([key, sound]) => (
                      <button
                        key={key}
                        onClick={() => {
                          updateSignupAlertSettings({ selected_sound: key });
                          playNotificationSound(key);
                        }}
                        className={`p-3 rounded-lg text-left transition-all ${
                          signupAlertSettings.selected_sound === key
                            ? 'bg-[#df1283]/30 border-2 border-[#df1283]'
                            : 'bg-white/5 border border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">{sound.icon}</span>
                          <span className="text-sm font-medium text-white">{sound.name}</span>
                        </div>
                        <p className="text-xs text-white/50">{sound.description}</p>
                        {signupAlertSettings.selected_sound === key && (
                          <span className="inline-block mt-1 text-xs text-[#0FFFE2]">✓ Selected</span>
                        )}
                      </button>
                    ))}
                    
                    {/* Custom Sound Option */}
                    <div
                      className={`p-3 rounded-lg text-left transition-all ${
                        signupAlertSettings.selected_sound === 'custom'
                          ? 'bg-[#df1283]/30 border-2 border-[#df1283]'
                          : 'bg-white/5 border border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">📁</span>
                        <span className="text-sm font-medium text-white">Custom Sound</span>
                      </div>
                      <p className="text-xs text-white/50 mb-2">Upload your own (MP3, max 1MB)</p>
                      {signupAlertSettings.custom_sound_url ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => playNotificationSound('custom')}
                            className="text-xs px-2 py-1 bg-[#0FFFE2]/20 text-[#0FFFE2] rounded"
                          >
                            ▶ Play
                          </button>
                          <label className="text-xs px-2 py-1 bg-white/10 text-white/70 rounded cursor-pointer hover:bg-white/20">
                            Replace
                            <input
                              type="file"
                              accept="audio/*"
                              className="hidden"
                              onChange={handleCustomSoundUpload}
                            />
                          </label>
                          {signupAlertSettings.selected_sound === 'custom' && (
                            <span className="text-xs text-[#0FFFE2]">✓</span>
                          )}
                        </div>
                      ) : (
                        <label className="inline-block text-xs px-2 py-1 bg-[#FF7901]/20 text-[#FF7901] rounded cursor-pointer hover:bg-[#FF7901]/30">
                          <Upload className="w-3 h-3 inline mr-1" />
                          Upload
                          <input
                            type="file"
                            accept="audio/*"
                            className="hidden"
                            onChange={handleCustomSoundUpload}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-white/40 text-center">Click any sound to preview and select it</p>
                </div>
              )}
              
              {/* Signup Alerts List */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {signupAlerts.length === 0 ? (
                  <div className="text-center py-8 text-[#FDF2F8]/40">
                    <UserPlus className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No recent signups</p>
                    <p className="text-xs mt-1">New members will appear here in real-time</p>
                  </div>
                ) : (
                  signupAlerts.map((alert, index) => (
                    <div 
                      key={alert.user_id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        index === 0 ? 'bg-[#0FFFE2]/10 border border-[#0FFFE2]/30' : 'bg-[#4A0A66]/30 hover:bg-[#4A0A66]/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          alert.partnership_id ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                          alert.tier === 'founding_member' ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                          alert.tier === 'premium' ? 'bg-[#df1283]/20 text-[#df1283]' :
                          'bg-[#0FFFE2]/20 text-[#0FFFE2]'
                        }`}>
                          {alert.partnership_id ? <Building2 className="w-5 h-5" /> :
                           alert.tier === 'founding_member' ? <Star className="w-5 h-5" /> :
                           alert.tier === 'premium' ? <Crown className="w-5 h-5" /> :
                           <User className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">{alert.name}</p>
                          <p className="text-xs text-[#FDF2F8]/50">{alert.email}</p>
                          {alert.partnership_name && (
                            <p className="text-xs text-[#FF7901]">via {alert.partnership_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        {/* Clickable tier badge with dropdown */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingUserTier(editingUserTier?.user_id === alert.user_id ? null : {
                                user_id: alert.user_id,
                                email: alert.email,
                                current_tier: alert.tier
                              });
                            }}
                            className={`text-xs px-2 py-1 rounded-full cursor-pointer hover:ring-2 hover:ring-white/30 transition-all ${
                              alert.partnership_id ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                              alert.tier === 'founding_member' ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                              alert.tier === 'premium' ? 'bg-[#df1283]/20 text-[#df1283]' :
                              alert.tier === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                              'bg-[#4A0A66] text-[#FDF2F8]/70'
                            }`}
                            title="Click to change tier"
                          >
                            {alert.partnership_id ? 'Partner' : alert.tier === 'cancelled' ? 'Cancelled' : alert.tier_label}
                          </button>
                          
                          {/* Tier dropdown */}
                          {editingUserTier?.user_id === alert.user_id && (
                            <div className="absolute right-0 top-8 z-50 bg-[#1a0527] border border-[#df1283]/30 rounded-lg shadow-xl p-2 min-w-[140px]">
                              <p className="text-xs text-gray-400 px-2 pb-1 border-b border-white/10 mb-1">Change tier:</p>
                              {['premium', 'free', 'cancelled'].map(tier => (
                                <button
                                  key={tier}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateUserTier(alert.user_id, tier);
                                  }}
                                  disabled={updatingTier || alert.tier === tier}
                                  className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-[#df1283]/20 transition-colors ${
                                    alert.tier === tier ? 'bg-[#df1283]/10 text-[#df1283]' : 'text-white'
                                  } ${updatingTier ? 'opacity-50' : ''}`}
                                >
                                  {tier === 'premium' ? '⭐ Premium' : 
                                   tier === 'free' ? '👤 Free' : 
                                   tier === 'cancelled' ? '🚫 Cancelled' : tier}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-[#FDF2F8]/40">
                          {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {alert.partnership_id && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.post(`${API_BASE}/api/admin/partnerships/resend-welcome-email/${alert.user_id}`, {}, { withCredentials: true });
                                toast.success(`Welcome email resent to ${alert.email}`);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to resend email');
                              }
                            }}
                            className="text-xs text-[#0FFFE2] hover:text-[#0FFFE2]/80 flex items-center gap-1"
                          >
                            <Mail className="w-3 h-3" /> Resend
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {signupAlerts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#4A0A66] flex items-center justify-between text-sm">
                  <span className="text-[#FDF2F8]/50">
                    Showing last {signupAlerts.length} signup{signupAlerts.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => setActiveTab('users')}
                    className="text-[#0FFFE2] hover:text-[#0FFFE2]/80 flex items-center gap-1"
                  >
                    View All Users <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              </div>
              )}
            </div>

            {/* Entry Analytics Deep Dive */}
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl overflow-hidden mb-6">
              <div className="p-6 pb-4 flex items-center justify-between">
                <button 
                  onClick={() => toggleSection('entryAnalytics')}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  {collapsedSections.entryAnalytics ? (
                    <ChevronRight className="w-5 h-5 text-[#FDF2F8]/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#FDF2F8]/60" />
                  )}
                  <BookOpen className="w-5 h-5 text-[#FF7901]" />
                  <h3 className="text-lg font-semibold text-white">Entry Analytics</h3>
                  <span className="px-2 py-0.5 bg-[#FF7901]/20 text-[#FF7901] text-xs rounded-full ml-2">
                    Deep Dive
                  </span>
                </button>
                <div className="flex items-center gap-3">
                  {/* Date Range Selector */}
                  <select
                    value={analyticsDateRange}
                    onChange={(e) => {
                      setAnalyticsDateRange(e.target.value);
                      if (e.target.value !== 'custom') {
                        fetchEntryAnalytics(e.target.value);
                      }
                    }}
                    className="bg-[#4A0A66]/50 border border-[#4A0A66] text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#df1283] cursor-pointer"
                  >
                    <option value="today">📅 Today</option>
                    <option value="yesterday">📅 Yesterday</option>
                    <option value="7days">📅 Last 7 Days</option>
                    <option value="30days">📅 Last 30 Days</option>
                    <option value="90days">📅 Last 90 Days</option>
                    <option value="all">📅 All Time</option>
                    <option value="custom">📅 Custom Range</option>
                  </select>
                  {analyticsDateRange === 'custom' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="bg-[#4A0A66]/50 text-white text-xs px-2 py-1.5 rounded-lg border border-[#4A0A66] focus:outline-none focus:border-[#df1283] [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:invert"
                      />
                      <span className="text-[#FDF2F8]/50 text-xs">to</span>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="bg-[#4A0A66]/50 text-white text-xs px-2 py-1.5 rounded-lg border border-[#4A0A66] focus:outline-none focus:border-[#df1283] [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:invert"
                      />
                      <Button
                        onClick={applyCustomDateRange}
                        size="sm"
                        className="bg-[#df1283] text-white hover:bg-[#df1283]/80 text-xs px-2 py-1"
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                  <Button
                    onClick={() => fetchEntryAnalytics(analyticsDateRange)}
                    variant="ghost"
                    size="sm"
                    className="text-[#FDF2F8]/60 hover:text-white"
                    disabled={loadingEntryAnalytics}
                  >
                    {loadingEntryAnalytics ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {!collapsedSections.entryAnalytics && (
              <div className="px-6 pb-6">
              {loadingEntryAnalytics ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#FF7901]" />
                </div>
              ) : entryAnalytics ? (
                <>
                  {/* Row 1: Key Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                      <p className="text-xs text-[#FDF2F8]/60 mb-1">Avg Words/Entry</p>
                      <p className="text-2xl font-bold text-white">{entryAnalytics.word_stats?.avg_word_count || 0}</p>
                      <p className="text-xs text-[#FDF2F8]/40">Longest: {entryAnalytics.word_stats?.longest_entry || 0}</p>
                    </div>
                    <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                      <p className="text-xs text-[#FDF2F8]/60 mb-1">Avg Entries/User</p>
                      <p className="text-2xl font-bold text-white">{entryAnalytics.summary?.avg_entries_per_user || 0}</p>
                      <p className="text-xs text-[#FDF2F8]/40">{entryAnalytics.summary?.unique_users || 0} active users</p>
                    </div>
                    <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                      <p className="text-xs text-[#FDF2F8]/60 mb-1">Peak Writing Hour</p>
                      <p className="text-2xl font-bold text-[#22C55E]">{formatHourTo12(entryAnalytics.timing?.peak_hour)}</p>
                      <p className="text-xs text-[#FDF2F8]/40">
                        {analyticsDateRange === 'today' ? "Today's peak" : 
                         analyticsDateRange === '7days' ? 'Last 7 days' :
                         analyticsDateRange === '30days' ? 'Last 30 days' :
                         analyticsDateRange === 'all' ? 'All time' : 'Selected range'}
                      </p>
                    </div>
                    <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                      <p className="text-xs text-[#FDF2F8]/60 mb-1">Busiest Day</p>
                      <p className="text-2xl font-bold text-[#8B5CF6]">{entryAnalytics.timing?.busiest_day || 'N/A'}</p>
                      <p className="text-xs text-[#FDF2F8]/40">
                        {entryAnalytics.timing?.busiest_day_count ? `${entryAnalytics.timing.busiest_day_count} entries` : ''} 
                        {' '}({analyticsDateRange === 'today' ? "today" : 
                         analyticsDateRange === 'yesterday' ? "yesterday" :
                         analyticsDateRange === '7days' ? 'last 7 days' :
                         analyticsDateRange === '30days' ? 'last 30 days' :
                         analyticsDateRange === '90days' ? 'last 90 days' :
                         analyticsDateRange === 'all' ? 'all time' : 'selected range'})
                      </p>
                    </div>
                  </div>

                  {/* Row 2: Voice vs Manual + AI Coach */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Voice vs Manual */}
                    <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                      <p className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                        <Mic className="w-4 h-4 text-[#0FFFE2]" />
                        Input Method
                      </p>
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-[#0FFFE2]">Voice Typing</span>
                            <span className="text-white">{entryAnalytics.input_methods?.voice_percentage || 0}%</span>
                          </div>
                          <div className="h-2 bg-[#1a0527] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#0FFFE2] to-[#22C55E] rounded-full"
                              style={{ width: `${entryAnalytics.input_methods?.voice_percentage || 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-[#FDF2F8]/50 mt-2">
                        <span>Voice: {entryAnalytics.input_methods?.voice_entries || 0}</span>
                        <span>Manual: {entryAnalytics.input_methods?.manual_entries || 0}</span>
                      </div>
                    </div>

                    {/* AI Coach Usage */}
                    <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                      <p className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                        AI Coach Reflections
                      </p>
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-[#F59E0B]">Usage Rate</span>
                            <span className="text-white">{entryAnalytics.ai_coach?.reflection_rate || 0}%</span>
                          </div>
                          <div className="h-2 bg-[#1a0527] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#F59E0B] to-[#EF4444] rounded-full"
                              style={{ width: `${entryAnalytics.ai_coach?.reflection_rate || 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-[#FDF2F8]/50 mt-2">
                        {entryAnalytics.ai_coach?.reflections_generated || 0} reflections generated
                      </p>
                    </div>
                  </div>

                  {/* Row 3: Day of Week Distribution */}
                  {entryAnalytics.timing?.days_distribution?.length > 0 && (
                    <div className="mt-4 bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-white flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#8B5CF6]" />
                          Entries by Day of Week
                          <span className="text-xs text-[#FDF2F8]/40 font-normal">
                            ({entryAnalytics.timing.days_distribution.reduce((sum, d) => sum + (d.count || 0), 0)} entries in period)
                          </span>
                        </p>
                        <span className="text-xs text-[#FDF2F8]/40">
                          {analyticsDateRange === 'today' ? "Today only" : 
                           analyticsDateRange === 'yesterday' ? "Yesterday only" :
                           analyticsDateRange === '7days' ? 'Last 7 Days' :
                           analyticsDateRange === '30days' ? 'Last 30 Days' :
                           analyticsDateRange === '90days' ? 'Last 90 Days' :
                           analyticsDateRange === 'all' ? 'All Time' : 'Custom Range'}
                        </span>
                      </div>
                      {(analyticsDateRange === 'today' || analyticsDateRange === 'yesterday') && (
                        <p className="text-xs text-[#F59E0B]/70 mb-3 flex items-center gap-1">
                          ⚠️ Single-day view only shows that day's entries. Select "Last 7 Days" or longer for weekly patterns.
                        </p>
                      )}
                      <div className="flex items-end justify-between gap-2 h-24">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => {
                          const dayData = entryAnalytics.timing.days_distribution.find(d => d.day_num === idx + 1);
                          const count = dayData?.count || 0;
                          const maxCount = Math.max(...entryAnalytics.timing.days_distribution.map(d => d.count));
                          const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                          const isBusiest = entryAnalytics.timing?.busiest_day === ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][idx];
                          return (
                            <div key={day} className="flex-1 flex flex-col items-center">
                              <div 
                                className={`w-full rounded-t ${isBusiest ? 'bg-gradient-to-t from-[#22C55E] to-[#4ADE80]' : 'bg-gradient-to-t from-[#8B5CF6] to-[#A855F7]'}`}
                                style={{ height: `${Math.max(height, 4)}%`, minHeight: '4px' }}
                              />
                              <span className={`text-[10px] mt-1 ${isBusiest ? 'text-[#22C55E]' : 'text-[#FDF2F8]/50'}`}>{day}</span>
                              <span className={`text-[10px] ${isBusiest ? 'text-[#22C55E] font-bold' : 'text-white'}`}>{count}</span>
                            </div>
                          );
                        })}
                      </div>
                      {entryAnalytics.timing?.busiest_day && entryAnalytics.timing?.busiest_day !== 'N/A' && (
                        <p className="text-xs text-[#FDF2F8]/50 mt-2 text-center">
                          🏆 Busiest day: <span className="text-[#22C55E] font-medium">{entryAnalytics.timing.busiest_day}</span>
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-[#FDF2F8]/50 py-8">No entry data available</p>
              )}
              </div>
              )}
            </div>

            {/* World Map - Collapsible */}
            <CollapsibleSection
              id="world-map"
              title="User Locations"
              icon={Globe}
              iconColor="#0FFFE2"
              defaultOpen={true}
            >
              <div className="relative" style={{ zIndex: 1 }}>
                <WorldMap locations={locations} />
              </div>
            </CollapsibleSection>

            {/* Traffic Sources - Collapsible */}
            <CollapsibleSection
              id="traffic-sources"
              title="How Did You Find Us"
              icon={Link2}
              iconColor="#FF7901"
              defaultOpen={true}
            >
              <div className="grid md:grid-cols-2 gap-4">
                {/* Referral Sources Bar Chart */}
                <ReferralSourceChart data={referralStats} />
                
                {/* Traffic Sources Pie Chart */}
                <TrafficSourcesPieChart 
                  data={referralStats} 
                  onDateChange={handleDateRangeChange}
                  selectedDateRange={referralDateRange}
                />
              </div>
            </CollapsibleSection>

            {/* Conversion Tracking Dashboard - Collapsible */}
            <CollapsibleSection
              id="conversion-tracking"
              title="Conversion Tracking"
              icon={Target}
              iconColor="#0FFFE2"
              defaultOpen={false}
              badge="Facebook Pixel"
            >
              <div className="flex justify-end mb-4">
                <span className="text-xs text-[#FDF2F8]/40">Pixel ID: 1425815291009956</span>
              </div>
              
              {/* Conversion Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#22c55e]/20 flex items-center justify-center">
                      <UserPlus className="w-4 h-4 text-[#22c55e]" />
                    </div>
                    <span className="text-xs text-[#FDF2F8]/50">Signups</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.new_users_today || 0}</div>
                  <div className="text-xs text-[#22c55e]">CompleteRegistration</div>
                </div>
                
                <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#3b82f6]/20 flex items-center justify-center">
                      <LogIn className="w-4 h-4 text-[#3b82f6]" />
                    </div>
                    <span className="text-xs text-[#FDF2F8]/50">Logins</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.active_today || 0}</div>
                  <div className="text-xs text-[#3b82f6]">Login Events</div>
                </div>
                
                <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#FF7901]/20 flex items-center justify-center">
                      <MousePointerClick className="w-4 h-4 text-[#FF7901]" />
                    </div>
                    <span className="text-xs text-[#FDF2F8]/50">Leads</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.leads_today || analytics.new_users_today || 0}</div>
                  <div className="text-xs text-[#FF7901]">Lead Events</div>
                </div>
                
                <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#df1283]/20 flex items-center justify-center">
                      <Eye className="w-4 h-4 text-[#df1283]" />
                    </div>
                    <span className="text-xs text-[#FDF2F8]/50">Page Views</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.total_visits || '-'}</div>
                  <div className="text-xs text-[#df1283]">PageView Events</div>
                </div>
              </div>
              
              {/* Conversion Funnel */}
              <div className="bg-[#4A0A66]/20 rounded-xl p-4 border border-[#4A0A66]/30">
                <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#0FFFE2]" />
                  Conversion Funnel
                </h4>
                <div className="flex items-center justify-between gap-2">
                  {[
                    { label: 'Page Views', value: analytics.total_visits || 0, color: '#df1283' },
                    { label: 'Initiated Signup', value: Math.round((analytics.new_users_today || 0) * 1.5), color: '#FF7901' },
                    { label: 'Completed Signup', value: analytics.new_users_today || 0, color: '#22c55e' },
                    { label: 'First Entry', value: Math.round((analytics.new_users_today || 0) * 0.6), color: '#0FFFE2' }
                  ].map((step, idx, arr) => (
                    <div key={step.label} className="flex-1 text-center">
                      <div 
                        className="h-16 rounded-lg mb-2 flex items-end justify-center"
                        style={{ 
                          background: `linear-gradient(to top, ${step.color}30, ${step.color}10)`,
                          borderBottom: `3px solid ${step.color}`
                        }}
                      >
                        <span className="text-lg font-bold text-white mb-1">{step.value}</span>
                      </div>
                      <span className="text-xs text-[#FDF2F8]/60">{step.label}</span>
                      {idx < arr.length - 1 && (
                        <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2">
                          <ChevronRight className="w-4 h-4 text-[#FDF2F8]/20" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-xs text-[#FDF2F8]/40 mt-4 text-center">
                Events are tracked via Facebook Pixel. View detailed analytics in your{' '}
                <a 
                  href="https://business.facebook.com/events_manager" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#0FFFE2] hover:underline"
                >
                  Meta Events Manager
                </a>
              </p>
            </CollapsibleSection>

            {/* Mood & Leaderboards - Collapsible */}
            <CollapsibleSection
              id="mood-leaderboards"
              title="Mood & Leaderboards"
              icon={Trophy}
              iconColor="#FF7901"
              defaultOpen={true}
            >
              <div className="grid md:grid-cols-3 gap-4">
                <MoodChart data={analytics.mood_distribution} />
                
                {/* Top Users */}
                <div className="bg-[#4A0A66]/30 border border-[#4A0A66]/50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FF7901]" />
                    Top Users by Entries
                  </h3>
                  <div className="space-y-2">
                    {analytics.top_users?.slice(0, 5).map((user, idx) => (
                      <div key={user.user_id || user.email || `top-user-${idx}`} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-[#df1283]">#{idx + 1}</span>
                          <span className="text-sm text-white truncate">{user.name}</span>
                        </div>
                        <span className="text-xs text-[#FF7901]">{user.entries}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Streak Leaders */}
                <div className="bg-[#4A0A66]/30 border border-[#4A0A66]/50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-500" />
                    Streak Leaderboard
                  </h3>
                  <div className="space-y-2">
                    {analytics.streak_leaders?.slice(0, 5).map((user, idx) => (
                      <div key={user.user_id || user.email || `streak-leader-${idx}`} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-orange-400">#{idx + 1}</span>
                          <span className="text-sm text-white truncate">{user.name}</span>
                        </div>
                        <span className="text-xs text-orange-400 flex items-center gap-1">
                          <Flame className="w-3 h-3" />{user.streak}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Users List - Collapsible */}
            <CollapsibleSection
              id="all-users"
              title={`All Users (${users.length})`}
              icon={Users}
              iconColor="#df1283"
              defaultOpen={true}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                  <Button
                    onClick={() => setShowCreateUserModal(true)}
                    size="sm"
                    className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90 h-8 text-xs"
                    data-testid="dashboard-add-member-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add Member
                  </Button>
                  <Button 
                    onClick={handleExportCSV} 
                    variant="outline" 
                    size="sm" 
                    className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20 h-8 text-xs"
                    data-testid="download-users-csv-btn"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download CSV
                  </Button>
                  <label className="flex items-center gap-2 text-xs text-[#FDF2F8]/60 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={includeB2BUsers}
                      onChange={async (e) => {
                        setIncludeB2BUsers(e.target.checked);
                        try {
                          const usersRes = await axios.get(`${API_BASE}/api/admin/users?include_b2b=${e.target.checked}`);
                          setUsers(usersRes.data);
                        } catch (err) {
                          console.error('Error fetching users:', err);
                        }
                      }}
                      className="rounded border-[#4A0A66] bg-[#4A0A66]/30 text-[#df1283] focus:ring-[#df1283]"
                    />
                    Include B2B Users
                  </label>
                </div>
              </div>
              
              {/* User Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] text-white pl-10 pr-4 py-2 rounded-lg text-sm placeholder:text-[#FDF2F8]/40 focus:outline-none focus:border-[#df1283]"
                  data-testid="user-search-input"
                />
                {userSearchQuery && (
                  <button 
                    onClick={() => setUserSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FDF2F8]/40 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              <ScrollArea className="h-[300px]">
                <div className="space-y-2 pr-4">
                  {users
                    .filter(user => {
                      if (!userSearchQuery) return true;
                      const query = userSearchQuery.toLowerCase();
                      const name = (user.first_name || '').toLowerCase() + ' ' + (user.last_name || '').toLowerCase();
                      const email = (user.email || '').toLowerCase();
                      return name.includes(query) || email.includes(query);
                    })
                    .map((user, index) => (
                    <UserRow 
                      key={user.user_id} 
                      user={user} 
                      index={index}
                      onDelete={handleDeleteUser}
                      onToggleStatus={handleToggleUserStatus}
                      onViewDetails={setSelectedUserId}
                      onChangeTier={updateUserTier}
                    />
                  ))}
                  {userSearchQuery && users.filter(user => {
                    const query = userSearchQuery.toLowerCase();
                    const name = (user.first_name || '').toLowerCase() + ' ' + (user.last_name || '').toLowerCase();
                    const email = (user.email || '').toLowerCase();
                    return name.includes(query) || email.includes(query);
                  }).length === 0 && (
                    <div className="text-center text-[#FDF2F8]/50 py-8">
                      No users found matching "{userSearchQuery}"
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CollapsibleSection>

            {/* Banner Management */}
            <div className="mt-4">
              <CollapsibleSection 
                id="banner-management" 
                title="Banner Management" 
                icon={ImageIcon}
                iconColor="#FF7901"
                defaultOpen={true}
              >
                <BannerManager />
              </CollapsibleSection>
            </div>
            
            {/* Motivational Videos Management */}
            <div className="mt-4">
              <CollapsibleSection 
                id="motivational-videos" 
                title="Motivational Videos" 
                icon={Video}
                iconColor="#df1283"
                defaultOpen={true}
              >
                <MotivationalVideosManager />
              </CollapsibleSection>
            </div>
          </>
        )}

        {/* Modals */}
        {selectedUserId && (
          <UserDetailModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
        )}
        {showVoiceModal && (
          <VoiceUsersModal 
            onClose={() => setShowVoiceModal(false)} 
            onUserClick={(user) => {
              setShowVoiceModal(false);
              fetchUserDetails(user);
            }}
          />
        )}
        {showAIModal && (
          <AIUsersModal 
            onClose={() => setShowAIModal(false)} 
            onUserClick={(user) => {
              setShowAIModal(false);
              fetchUserDetails(user);
            }}
          />
        )}
        
        {/* Global User Detail Modal - Opens from any tab when a user is clicked */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" data-testid="user-detail-modal">
            <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-[#4A0A66]/50">
                <div className="flex items-center gap-4">
                  {selectedUser.picture ? (
                    <img src={selectedUser.picture} alt="" className="w-14 h-14 rounded-full" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-xl font-bold">
                      {(selectedUser.first_name || selectedUser.name || selectedUser.email || 'U')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedUser.first_name || selectedUser.name || 'User'}</h3>
                    <p className="text-sm text-[#FDF2F8]/50">{selectedUser.email}</p>
                  </div>
                </div>
                <button 
                  onClick={() => { setSelectedUser(null); setUserEmailHistory([]); }}
                  className="p-2 text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
                  data-testid="close-user-detail-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {loadingUserDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FFFE2]"></div>
                  </div>
                ) : (
                  <>
                    {/* User Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-[#0FFFE2]">{selectedUser.entry_count || 0}</div>
                        <div className="text-xs text-[#FDF2F8]/50">Journal Entries</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-[#FF7901]">{selectedUser.streak || 0}</div>
                        <div className="text-xs text-[#FDF2F8]/50">Day Streak</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-white">{userEmailHistory.filter(e => e.event_type === 'open').length}</div>
                        <div className="text-xs text-[#FDF2F8]/50">Emails Opened</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-white">{userEmailHistory.filter(e => e.event_type === 'click').length}</div>
                        <div className="text-xs text-[#FDF2F8]/50">Links Clicked</div>
                      </div>
                    </div>
                    
                    {/* Status Badges */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {selectedUser.email_unsubscribed && (
                        <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">Unsubscribed</span>
                      )}
                      {selectedUser.email_bounced && (
                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium">Email Bounced</span>
                      )}
                      {selectedUser.referral_source && (
                        <span className="px-3 py-1 bg-[#0FFFE2]/20 text-[#0FFFE2] rounded-full text-xs font-medium">
                          Source: {selectedUser.referral_source}
                        </span>
                      )}
                      {selectedUser.created_at && (
                        <span className="px-3 py-1 bg-[#4A0A66]/30 text-[#FDF2F8]/70 rounded-full text-xs">
                          Joined: {format(new Date(selectedUser.created_at), 'MMM d, yyyy')}
                        </span>
                      )}
                      {selectedUser.last_login && (
                        <span className="px-3 py-1 bg-[#4A0A66]/30 text-[#FDF2F8]/70 rounded-full text-xs">
                          Last login: {format(new Date(selectedUser.last_login), 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>

                    {/* UTM Traffic Source Tracking - Always visible */}
                    <div className="mb-6" data-testid="global-user-utm-tracking-section">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#0FFFE2]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                        Traffic Source (UTM)
                      </h4>
                      {(selectedUser.utm_source || selectedUser.utm_medium || selectedUser.utm_campaign || selectedUser.utm_term || selectedUser.utm_content) ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {selectedUser.utm_source && (
                            <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="global-utm-source-display">
                              <div className="text-xs text-[#FDF2F8]/50 mb-1">Source</div>
                              <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_source}>
                                {selectedUser.utm_source}
                              </div>
                            </div>
                          )}
                          {selectedUser.utm_medium && (
                            <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="global-utm-medium-display">
                              <div className="text-xs text-[#FDF2F8]/50 mb-1">Medium</div>
                              <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_medium}>
                                {selectedUser.utm_medium}
                              </div>
                            </div>
                          )}
                          {selectedUser.utm_campaign && (
                            <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="global-utm-campaign-display">
                              <div className="text-xs text-[#FDF2F8]/50 mb-1">Campaign</div>
                              <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_campaign}>
                                {selectedUser.utm_campaign}
                              </div>
                            </div>
                          )}
                          {selectedUser.utm_term && (
                            <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="global-utm-term-display">
                              <div className="text-xs text-[#FDF2F8]/50 mb-1">Term</div>
                              <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_term}>
                                {selectedUser.utm_term}
                              </div>
                            </div>
                          )}
                          {selectedUser.utm_content && (
                            <div className="bg-[#4A0A66]/20 rounded-lg p-3" data-testid="global-utm-content-display">
                              <div className="text-xs text-[#FDF2F8]/50 mb-1">Content</div>
                              <div className="text-sm text-white font-medium truncate" title={selectedUser.utm_content}>
                                {selectedUser.utm_content}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-[#FDF2F8]/40 text-sm text-center py-3 bg-[#4A0A66]/10 rounded-lg">No UTM tracking data available for this user</p>
                      )}
                    </div>
                    
                    {/* Email Activity History */}
                    <div>
                      <h4 className="text-white font-medium mb-3">Email Activity History</h4>
                      {userEmailHistory.length === 0 ? (
                        <div className="text-center py-6 text-[#FDF2F8]/40">
                          <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">No email activity recorded</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {userEmailHistory.map((event, idx) => (
                            <div key={event.id || `email-hist-${event.timestamp || idx}`} className="flex items-start gap-3 bg-[#4A0A66]/10 rounded-lg p-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                event.event_type === 'open' ? 'bg-[#0FFFE2]/20' : 'bg-[#FF7901]/20'
                              }`}>
                                {event.event_type === 'open' 
                                  ? <Eye className="w-4 h-4 text-[#0FFFE2]" />
                                  : <MousePointer className="w-4 h-4 text-[#FF7901]" />
                                }
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white">
                                  {event.event_type === 'open' ? 'Opened' : 'Clicked'}: <span className="text-[#FDF2F8]/70">{event.campaign_subject}</span>
                                </p>
                                {event.url && (
                                  <p className="text-xs text-[#FDF2F8]/40 truncate">{event.url}</p>
                                )}
                                <p className="text-xs text-[#FDF2F8]/30 mt-1">
                                  {format(new Date(event.timestamp), 'MMM d, yyyy \'at\' h:mm a')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <div className="p-6 border-t border-[#4A0A66]/50 bg-[#1a0527]/50">
                <button
                  onClick={() => { setSelectedUser(null); setUserEmailHistory([]); }}
                  className="w-full py-2 bg-[#4A0A66]/50 hover:bg-[#4A0A66] text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal for Overview tab */}
        <CreateUserModal 
          isOpen={showCreateUserModal}
          onClose={() => setShowCreateUserModal(false)}
          onUserCreated={async () => {
            // Refresh users list after creating a new user
            try {
              const usersRes = await axios.get(`${API_BASE}/api/admin/users?include_b2b=${includeB2BUsers}`);
              setUsers(usersRes.data);
            } catch (err) {
              console.error('Error refreshing users:', err);
            }
          }}
        />

        {/* Daily Breakdown Modal */}
        {showDailyBreakdownModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowDailyBreakdownModal(false)}>
            <div 
              className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]/50">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#22C55E]" />
                  Daily Signups Breakdown
                </h3>
                <button onClick={() => setShowDailyBreakdownModal(false)} className="text-[#FDF2F8]/50 hover:text-white p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {dailyBreakdown && dailyBreakdown.length > 0 ? (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#22C55E]">
                          {dailyBreakdown.reduce((sum, d) => sum + d.new_users, 0)}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Total New Users</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#df1283]">
                          {dailyBreakdown.reduce((sum, d) => sum + d.entries, 0)}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Total Entries</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#FF7901]">
                          {dailyBreakdown.reduce((sum, d) => sum + d.logins, 0)}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Total Logins</div>
                      </div>
                    </div>

                    {/* Daily Rows */}
                    <div className="space-y-2">
                      {dailyBreakdown.map((day) => (
                        <div 
                          key={day.date}
                          className={`bg-[#4A0A66]/20 rounded-xl p-4 cursor-pointer hover:bg-[#4A0A66]/30 transition-colors border ${selectedDayDetails?.date === day.date ? 'border-[#22C55E]' : 'border-transparent'}`}
                          onClick={() => setSelectedDayDetails(selectedDayDetails?.date === day.date ? null : day)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="text-white font-semibold w-28">{day.day_name}</div>
                              <div className="flex items-center gap-6">
                                <span className="flex items-center gap-1 text-[#22C55E]">
                                  <UserPlus className="w-4 h-4" />
                                  <span className="font-bold">{day.new_users}</span>
                                  <span className="text-xs text-[#FDF2F8]/50">signups</span>
                                </span>
                                <span className="flex items-center gap-1 text-[#df1283]">
                                  <BookOpen className="w-4 h-4" />
                                  <span className="font-bold">{day.entries}</span>
                                  <span className="text-xs text-[#FDF2F8]/50">entries</span>
                                </span>
                                <span className="flex items-center gap-1 text-[#FF7901]">
                                  <LogIn className="w-4 h-4" />
                                  <span className="font-bold">{day.logins}</span>
                                  <span className="text-xs text-[#FDF2F8]/50">logins</span>
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-[#FDF2F8]/40">
                              {day.new_users > 0 ? 'Click to see users' : ''}
                            </div>
                          </div>

                          {/* Expanded User Details */}
                          {selectedDayDetails?.date === day.date && day.new_user_details?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[#4A0A66]/50">
                              <h4 className="text-sm font-semibold text-[#22C55E] mb-3">New Users on {day.day_name}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {day.new_user_details.map((user) => (
                                  <div key={user.user_id} className="flex items-center gap-3 bg-[#1a0527] rounded-lg p-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#22C55E] to-green-600 flex items-center justify-center text-white text-sm font-bold">
                                      {(user.first_name || user.name || user.email || 'U')[0].toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-white truncate">{user.first_name || user.name || 'Anonymous'}</p>
                                      <p className="text-xs text-[#FDF2F8]/50 truncate">{user.email}</p>
                                    </div>
                                    {user.referral_source && (
                                      <span className="text-xs bg-[#FF7901]/20 text-[#FF7901] px-2 py-0.5 rounded-full truncate max-w-24">
                                        {user.referral_source}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#FDF2F8]/40">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No daily data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Entries Breakdown Modal */}
        {showEntriesBreakdownModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowEntriesBreakdownModal(false)}>
            <div 
              className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]/50">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#FF7901]" />
                  Daily Entries Breakdown
                </h3>
                <button onClick={() => setShowEntriesBreakdownModal(false)} className="text-[#FDF2F8]/50 hover:text-white p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {dailyBreakdown && dailyBreakdown.length > 0 ? (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#FF7901]">
                          {dailyBreakdown.reduce((sum, d) => sum + d.entries, 0)}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Total Entries</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#df1283]">
                          {dailyBreakdown.filter(d => d.entries > 0).length}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Days with Entries</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#22C55E]">
                          {Math.round(dailyBreakdown.reduce((sum, d) => sum + d.entries, 0) / (dailyBreakdown.length || 1) * 10) / 10}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Avg/Day</div>
                      </div>
                    </div>

                    {/* Daily Rows */}
                    <div className="space-y-2">
                      {dailyBreakdown.map((day) => (
                        <div 
                          key={day.date}
                          className={`bg-[#4A0A66]/20 rounded-xl p-4 cursor-pointer hover:bg-[#4A0A66]/30 transition-colors border ${selectedDayEntries?.date === day.date ? 'border-[#FF7901]' : 'border-transparent'}`}
                          onClick={() => setSelectedDayEntries(selectedDayEntries?.date === day.date ? null : day)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="text-white font-semibold w-28">{day.day_name}</div>
                              <div className="flex items-center gap-6">
                                <span className="flex items-center gap-1 text-[#FF7901]">
                                  <BookOpen className="w-4 h-4" />
                                  <span className="font-bold">{day.entries}</span>
                                  <span className="text-xs text-[#FDF2F8]/50">entries</span>
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-[#FDF2F8]/40">
                              {day.entries > 0 ? 'Click to see entries' : ''}
                            </div>
                          </div>

                          {/* Expanded Entry Details */}
                          {selectedDayEntries?.date === day.date && day.entry_details?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[#4A0A66]/50">
                              <h4 className="text-sm font-semibold text-[#FF7901] mb-3">Journal Entries on {day.day_name}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {day.entry_details.map((entry, idx) => (
                                  <div key={entry.entry_id || idx} className="flex items-center gap-3 bg-[#1a0527] rounded-lg p-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF7901] to-orange-600 flex items-center justify-center text-white text-sm font-bold">
                                      {(entry.user_name || entry.user_email || 'U')[0].toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-white truncate">{entry.user_name || 'Anonymous'}</p>
                                      <p className="text-xs text-[#FDF2F8]/50 truncate">{entry.user_email}</p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {entry.mood && (
                                        <span className="text-lg">{entry.mood}</span>
                                      )}
                                      {entry.has_ai_reflection && (
                                        <span className="text-xs bg-[#8B5CF6]/20 text-[#8B5CF6] px-2 py-0.5 rounded-full">AI</span>
                                      )}
                                      {entry.word_count && (
                                        <span className="text-xs text-[#FDF2F8]/40">{entry.word_count} words</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#FDF2F8]/40">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No entries data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Logins Breakdown Modal */}
        {showLoginsBreakdownModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowLoginsBreakdownModal(false)}>
            <div 
              className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]/50">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <LogIn className="w-5 h-5 text-[#8B5CF6]" />
                  Daily Logins Breakdown
                </h3>
                <button onClick={() => setShowLoginsBreakdownModal(false)} className="text-[#FDF2F8]/50 hover:text-white p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {dailyBreakdown && dailyBreakdown.length > 0 ? (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#8B5CF6]">
                          {dailyBreakdown.reduce((sum, d) => sum + d.logins, 0)}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Total Logins</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#df1283]">
                          {dailyBreakdown.filter(d => d.logins > 0).length}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Days with Logins</div>
                      </div>
                      <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-[#22C55E]">
                          {Math.round(dailyBreakdown.reduce((sum, d) => sum + d.logins, 0) / (dailyBreakdown.length || 1) * 10) / 10}
                        </div>
                        <div className="text-sm text-[#FDF2F8]/50">Avg/Day</div>
                      </div>
                    </div>

                    {/* Daily Rows */}
                    <div className="space-y-2">
                      {dailyBreakdown.map((day) => (
                        <div 
                          key={day.date}
                          className={`bg-[#4A0A66]/20 rounded-xl p-4 cursor-pointer hover:bg-[#4A0A66]/30 transition-colors border ${selectedDayLogins?.date === day.date ? 'border-[#8B5CF6]' : 'border-transparent'}`}
                          onClick={() => setSelectedDayLogins(selectedDayLogins?.date === day.date ? null : day)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="text-white font-semibold w-28">{day.day_name}</div>
                              <div className="flex items-center gap-6">
                                <span className="flex items-center gap-1 text-[#8B5CF6]">
                                  <LogIn className="w-4 h-4" />
                                  <span className="font-bold">{day.logins}</span>
                                  <span className="text-xs text-[#FDF2F8]/50">logins</span>
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-[#FDF2F8]/40">
                              {day.logins > 0 ? 'Click to see login details' : ''}
                            </div>
                          </div>

                          {/* Expanded Login Details */}
                          {selectedDayLogins?.date === day.date && day.login_details?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[#4A0A66]/50">
                              <h4 className="text-sm font-semibold text-[#8B5CF6] mb-3">Login Activity on {day.day_name}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {day.login_details.map((login, idx) => (
                                  <div key={login.user_id || login.user_email || `login-${idx}`} className="flex items-center gap-3 bg-[#1a0527] rounded-lg p-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8B5CF6] to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                                      {(login.user_name || login.user_email || 'U')[0].toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-white truncate">{login.user_name || 'Anonymous'}</p>
                                      <p className="text-xs text-[#FDF2F8]/50 truncate">{login.user_email}</p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {login.auth_method && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${login.auth_method === 'google_oauth' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                                          {login.auth_method === 'google_oauth' ? 'Google' : 'Email'}
                                        </span>
                                      )}
                                      {login.device_type && (
                                        <span className="text-xs text-[#FDF2F8]/40">{login.device_type}</span>
                                      )}
                                      {login.browser && (
                                        <span className="text-xs text-[#FDF2F8]/30">{login.browser}</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#FDF2F8]/40">
                    <LogIn className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No login data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="text-center py-6 text-[#FDF2F8]/30 text-xs border-t border-[#4A0A66]/30">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Link to="/privacy" className="text-[#0FFFE2]/70 hover:text-[#0FFFE2] transition-colors">Privacy Policy</Link>
          <span>•</span>
          <Link to="/terms" className="text-[#0FFFE2]/70 hover:text-[#0FFFE2] transition-colors">Terms of Service</Link>
        </div>
        <p>© {new Date().getFullYear()} UnCloned® Journal. All rights reserved.</p>
        <p className="mt-1">Admin Dashboard v1.0</p>
      </footer>
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText="Delete"
        cancelText="Cancel"
        loading={confirmDialog.loading}
      />
      
      {/* Email Template Edit Modal */}
      {showTemplateModal && editingTemplate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl border border-[#4A0A66] w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-[#4A0A66]">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white capitalize">
                  Edit {editingTemplate.template_type.replace('_', ' ')} Email
                </h3>
                <button 
                  onClick={() => setShowTemplateModal(false)} 
                  className="text-[#FDF2F8]/50 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between mb-6 p-4 bg-[#4A0A66]/30 rounded-lg">
                <div>
                  <div className="text-white font-medium">Email Enabled</div>
                  <div className="text-sm text-[#FDF2F8]/50">Turn on to send this email automatically</div>
                </div>
                <button
                  onClick={() => setEditingTemplate({...editingTemplate, enabled: !editingTemplate.enabled})}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    editingTemplate.enabled ? 'bg-[#0FFFE2]' : 'bg-[#4A0A66]'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    editingTemplate.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {/* Subject Line */}
              <div className="mb-6">
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">Subject Line</label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  placeholder="Email subject line..."
                />
              </div>
              
              {/* Available Variables - Show all variables from API */}
              <div className="mb-4 p-3 bg-[#FF7901]/10 border border-[#FF7901]/30 rounded-lg">
                <div className="text-sm text-[#FF7901] font-medium mb-2">Available Variables (Click to copy)</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {Object.entries(emailVariables).map(([category, vars]) => (
                    <div key={category} className="space-y-1">
                      <div className="text-xs text-[#FDF2F8]/40 uppercase">{category}</div>
                      {Object.keys(vars).map(varName => (
                        <code 
                          key={varName} 
                          className="block px-2 py-0.5 bg-[#4A0A66]/50 text-[#0FFFE2] rounded text-xs cursor-pointer hover:bg-[#4A0A66] truncate"
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${varName}}}`);
                            toast.success(`Copied {{${varName}}}`);
                          }}
                          title={vars[varName]}
                        >
                          {`{{${varName}}}`}
                        </code>
                      ))}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#FDF2F8]/50 mt-2">Click any variable to copy. Hover for description.</p>
              </div>
              
              {/* HTML Content Editor */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-[#FDF2F8]/70">Email Body</label>
                  <div className="flex items-center gap-1 bg-[#1a0527] rounded-lg p-1">
                    <button
                      onClick={() => setTemplateEditorMode('blocks')}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${templateEditorMode === 'blocks' ? 'bg-[#FF7901] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                    >
                      Blocks
                    </button>
                    <button
                      onClick={() => setTemplateEditorMode('code')}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${templateEditorMode === 'code' ? 'bg-[#FF7901] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                    >
                      Code
                    </button>
                  </div>
                </div>
                {templateEditorMode === 'blocks' ? (
                  <EmailBlockBuilder
                    value={editingTemplate.body_html}
                    onChange={(html) => setEditingTemplate({...editingTemplate, body_html: html})}
                  />
                ) : (
                  <textarea
                    value={editingTemplate.body_html}
                    onChange={(e) => setEditingTemplate({...editingTemplate, body_html: e.target.value})}
                    rows={20}
                    className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] text-white rounded-lg p-4 font-mono text-sm focus:outline-none focus:border-[#FF7901]"
                    placeholder="<div>Your email HTML content here...</div>"
                  />
                )}
              </div>
              
              {/* Preview Button */}
              <div className="mb-6">
                <Button
                  onClick={() => window.open(`/api/admin/email-templates/${editingTemplate.template_type}/preview?name=Sarah`, '_blank')}
                  variant="outline"
                  className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Email
                </Button>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="p-6 border-t border-[#4A0A66] flex justify-end gap-3">
              <Button
                onClick={() => setShowTemplateModal(false)}
                variant="outline"
                className="border-[#4A0A66] text-[#FDF2F8]/70"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateTemplate}
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
